import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ClipboardEvent, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { Link, NavLink, useNavigate, useParams, useSearchParams } from 'react-router';
import { formatPhoneNumber } from '@stitch/pbx-adapter/catalog';
import {
  api,
  type ChannelAccess,
  type ChannelListing,
  type ChatPresence,
  type ConversationDetail,
  type ConversationView,
  type CustomStatus,
  type MessageView,
  type MessagesInbox,
  type NotifyLevel,
  type PresenceList,
  type Reaction,
  type ReactionChange,
  type StatusChoice,
  type MessageFileView
} from '../../api/client';
import { EmojiPicker } from '../../components/EmojiPicker';
import { useTenant } from '../../components/Shell';
import { Button, Chip, Dialog, Field, LoadError, Loading, Notice, PageHeader, SelectField } from '../../components/ui';
import { MoveToTeamDialog } from '../../components/MoveToTeam';
import { useLiveSessions } from '../../lib/sessions';
import { useAuth } from '../../lib/auth';
import { clockTime, dateOnly, dayHeading, dayOf, fullWhen, plural } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { emojiName, quickEmoji } from '../../lib/emoji';
import { applyReaction, countsAsUnread, mentionsMe, setMyPresence, settleUnread, unreadMark, updatedMessage, useMessagesLive, usePresenceList } from '../../lib/messages';
import { initials } from '../../lib/session';
import { segments, shrinkPicture } from '../../lib/texting';
import { pastedFiles, useFileDrop } from '../../lib/fileDrop';
import { createPortal } from 'react-dom';
import { HeaderExtras, SystemMessageExtras, UnderHeaderExtras } from './MessagesExtras';
import { CallOfferCard } from '../../components/CallOffer';
// Coworkers' local time (1.12.13).
import { AuthorName, LateLine, ListLocalTime, localTimeText, useClock } from '../../components/messages/LocalTime';
import { Avatar, hueOf } from '../../components/messages/Avatar';
import { FileViewer, fileSize, kindWords, type ViewerFile } from '../../components/messages/FileViewer';
import { DownloadButton } from '../../components/messages/DownloadButton';
import { LinkPreviewCard } from '../../components/messages/LinkPreview';
import { CodeBlock } from '../../components/messages/CodeBlock';
import { inlineCode, messageBlocks, plainPreview, trimLink } from '../../lib/richtext';
import { usePasteAsCode } from '../../lib/pasteCode';
import { PhoneDms, PhoneHome, listToGoBackTo } from './MessagesPhone';
import { clearNotificationsFor } from '../../lib/alerts';
import { DesktopNotifyOffer } from '../../components/Notifications';
// Files up to 500 MB, sent in parts (1.12.16).
import { ONE_GO_BYTES, UploadCancelled, fileRules, sizeWords, uploadInParts, type UploadHandle } from '../../lib/bigUpload';
import { SmartContext, SmartSide, SmartThreadBar, SmartThreadCatchUp, SmartTriage, TextStateMark, useSmart } from '../../components/messages/Smart'; // smart messaging
// Channels for everyone, open or private, and webhooks' messages (1.12.19).
import { AppMessage } from '../../components/messages/AppCard';
import { ChannelPeoplePicker, ChannelWebhooks, KindChoice, channelIcon } from '../../components/messages/ChannelKinds';
// Shared texting (1.12.32): ring groups' and queues' texts, the summary lane, Call.
import { TextMedia } from '../../components/messages/TextMedia';
import { CallButton, SummaryLane, TeamBar, TeamFilterBar, TeamGroup, TeamMark, inFilter, useSuggestion, useSummaryOpen, useTeamFilter } from '../../components/messages/TeamTexts';

/**
 * Stitch Messages: the company's channels, direct messages and texts in one inbox, Discord-style
 * (in Stitch's own look). Messages arrive live; what people write is shown as text, never as
 * HTML, and is kept out of session recordings and live help (data-private).
 */

const errorText = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** Why Stitch sent an automatic text (1.12.32): STOP, START and HELP answers, and teams' texts. */
const AUTO_WHY: Record<string, string> = {
  stop: 'Stitch’s answer to STOP',
  start: 'Stitch’s answer to START',
  help: 'Stitch’s answer to HELP (Settings → Texts)',
  place: 'Their place in line, from the team’s texting rules',
  after_hours: 'The team’s after-hours reply',
  opening: 'The team’s opening text'
};

/** As the server names channels: lower-case words with dashes. */
function channelSlug(raw: string): string {
  return raw
    .normalize('NFKC')
    .toLowerCase()
    .trim()
    .replace(/^#+/, '')
    .replace(/\s+/g, '-')
    .replace(/[^\p{L}\p{N}_-]/gu, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/, '');
}

function useWide(): boolean {
  const query = '(min-width: 861px)';
  const [wide, setWide] = useState(() => typeof window === 'undefined' || window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query);
    const on = () => setWide(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return wide;
}

/** A conversation's preview from a message heard live (as the server words it). */
function previewOf(m: MessageView, myId: string): ConversationView['preview'] {
  return {
    // A webhook's name is whatever the service sent: marked as an app's, as the server does.
    author: m.author.kind === 'outside' ? null : m.author.kind === 'app' ? `${m.author.name.trim() || 'A webhook'} (app)` : m.author.name || null,
    mine: m.author.kind === 'person' && m.author.id === myId,
    body: m.body.slice(0, 160),
    attached: m.files.length > 0 || (m.text?.media.length ?? 0) > 0,
    at: m.createdAt
  };
}

/** What people type and haven't sent, per conversation, while the tab is open. */
const drafts = new Map<string, string>();

/** The person using Messages (for their own reactions, shown at once). */
const Me = createContext<{ id: string; name: string }>({ id: '', name: '' });
/** Whether they're one of the company's admins (they may delete webhooks' messages). */
const IsAdmin = createContext(false);

/** The inbox: on a phone, `view` is the phone app's Home (channels, direct messages and texts) or DMs tab. */
export function TenantMessages({ view = 'home' }: { view?: 'home' | 'dms' }) {
  const { tenant } = useTenant();
  const { state } = useAuth();
  const member = state?.memberships?.find((m) => m.tenant === tenant.domain.toLowerCase());
  if (!member || !state?.user) {
    // Stitch staff looking at a customer's portal: messages are the company's own.
    return (
      <>
        <PageHeader eyebrow={tenant.name} title="Messages" />
        <div className="content">
          <Notice icon="lock" title="Messages are private to the company">
            {tenant.name}’s channels, direct messages and texts are for its own people. Stitch staff don’t see them.
          </Notice>
        </div>
      </>
    );
  }
  return (
    <Me.Provider
      value={{
        id: state.user.id,
        name: state.user.name || state.user.email.split('@')[0]!
      }}
    >
      <Inbox domain={tenant.domain.toLowerCase()} company={tenant.name} myId={state.user.id} view={view} />
    </Me.Provider>
  );
}

function Inbox({ domain, company, myId, view }: { domain: string; company: string; myId: string; view: 'home' | 'dms' }) {
  const { conversation: openId } = useParams();
  const navigate = useNavigate();
  const wide = useWide();
  const [params, setParams] = useSearchParams();
  const base = `/t/${encodeURIComponent(domain)}/messages`;
  const [inbox, setInbox] = useState<MessagesInbox>();
  const [error, setError] = useState<Error>();
  const [dialog, setDialog] = useState<null | 'channel' | 'dm' | 'browse' | 'search' | 'text'>(null);
  /** A team's own + chose its number for "Text a phone number" (3147). */
  const [textPreset, setTextPreset] = useState<string | null>(null);
  /** Texts you archived, shown under the list (1.12.30). */
  const [showArchived, setShowArchived] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  /** How far each conversation has been read, as heard live: newer than a list that was loading meanwhile. */
  const readMarks = useRef(new Map<string, number>());

  const load = useCallback(async () => {
    const mark = unreadMark(domain);
    try {
      const r = await api.inbox(domain);
      const conversations = r.conversations.map((c) => {
        const seq = Math.max(c.lastReadSeq, readMarks.current.get(c.id) ?? 0);
        return seq > c.lastReadSeq
          ? {
              ...c,
              lastReadSeq: seq,
              unread: seq >= c.lastSeq ? 0 : c.unread,
              mentions: seq >= c.lastSeq ? 0 : c.mentions
            }
          : c;
      });
      setInbox({ ...r, conversations });
      settleUnread(domain, mark, r.unread);
      setError(undefined);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    }
  }, [domain]);
  useEffect(() => {
    void load();
  }, [load]);
  const loadSoon = useCallback(() => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void load(), 300);
  }, [load]);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  // The phone app's header asks for search with ?search=1.
  useEffect(() => {
    if (!params.has('search')) return;
    setDialog('search');
    const next = new URLSearchParams(params);
    next.delete('search');
    setParams(next, { replace: true });
  }, [params, setParams]);

  // On a wide screen, a conversation is always open: #general to start with.
  useEffect(() => {
    if (openId || !wide || !inbox?.conversations.length) return;
    const first = inbox.conversations.find((c) => c.isDefault) ?? inbox.conversations[0]!;
    navigate(`${base}/${first.id}`, { replace: true });
  }, [openId, wide, inbox, base, navigate]);

  useMessagesLive((e) => {
    if (e.type === 'reset') return void load();
    if (e.tenant !== domain) return;
    if (e.type === 'conversation') return loadSoon();
    if (e.type === 'message' && e.message && e.conversationId) {
      const m = e.message;
      const known = inbox?.conversations.some((c) => c.id === e.conversationId);
      // A new direct message or text conversation: the list loads again.
      if (!known) return loadSoon();
      const counts = countsAsUnread(m, myId);
      const looking = e.conversationId === openId && document.visibilityState === 'visible';
      setInbox((prev) =>
        prev
          ? {
              ...prev,
              conversations: prev.conversations.map((c) =>
                c.id === e.conversationId
                  ? {
                      ...c,
                      lastSeq: Math.max(c.lastSeq, m.seq),
                      lastMessageAt: m.createdAt,
                      unread: !counts || looking ? c.unread : c.unread + 1,
                      mentions: looking || !mentionsMe(m, myId) || (m.author.kind === 'person' && m.author.id === myId) ? c.mentions : c.mentions + 1,
                      preview: m.kind === 'system' || (m.threadRootId && !m.alsoInChannel) ? c.preview : previewOf(m, myId),
                      // An archived text conversation comes back when they text, or when you do (1.12.30).
                      archivedByMe: c.archivedByMe && !(m.text && (m.text.direction === 'in' || (m.author.kind === 'person' && m.author.id === myId)))
                    }
                  : c
              )
            }
          : prev
      );
    }
    if (e.type === 'read' && e.conversationId && typeof e.lastReadSeq === 'number') {
      const seq = e.lastReadSeq;
      readMarks.current.set(e.conversationId, Math.max(seq, readMarks.current.get(e.conversationId) ?? 0));
      setInbox((prev) =>
        prev
          ? {
              ...prev,
              unread: e.unread ?? prev.unread,
              conversations: prev.conversations.map((c) =>
                c.id === e.conversationId
                  ? {
                      ...c,
                      lastReadSeq: seq,
                      unread: seq >= c.lastSeq ? 0 : c.unread,
                      mentions: seq >= c.lastSeq ? 0 : c.mentions
                    }
                  : c
              )
            }
          : prev
      );
    }
  });

  const presence = usePresenceList(domain);
  const smart = useSmart(domain); // smart messaging: Spam, closed texts, archived channels, Done/Snooze/Later
  const open = inbox?.conversations.find((c) => c.id === openId);
  const channels = inbox?.conversations.filter((c) => c.kind === 'channel' && !smart.hidden(c.id, c.lastSeq)) ?? []; // smart messaging: Done and snoozed leave the list
  const dms = inbox?.conversations.filter((c) => c.kind === 'dm' && !smart.hidden(c.id, c.lastSeq)) ?? []; // smart messaging: Done and snoozed leave the list
  const allTexts = inbox?.conversations.filter((c) => c.kind === 'text' && !smart.hidden(c.id, c.lastSeq)) ?? []; // smart messaging: Spam has its own view
  // Texts you archived leave your list until that number texts again (1.12.30).
  const texts = allTexts.filter((c) => !c.archivedByMe);
  const archivedTexts = allTexts.filter((c) => c.archivedByMe);
  // Texts to a ring group's or queue's number go under the team (1.12.32), with Mine, Waiting and All.
  const myTexts = texts.filter((c) => !c.team);
  const teamTexts = texts.filter((c) => c.team);
  const teams = [...new Map(teamTexts.map((c) => [c.team!.extension, c.team!])).values()].sort((a, b) => a.name.localeCompare(b.name));
  const [teamFilter, setTeamFilter] = useTeamFilter();
  // Counted only in teams whose lists show (a queue you're signed out of folds away: 2984).
  const shownTeam = (t: NonNullable<(typeof teamTexts)[number]['team']>) => !(t.kind === 'callQueue' && !t.signedIn && !t.supervisor);
  const teamCounts = {
    mine: teamTexts.filter((c) => shownTeam(c.team!) && inFilter(c.team!, 'mine')).length,
    waiting: teamTexts.filter((c) => shownTeam(c.team!) && inFilter(c.team!, 'waiting')).length,
    all: teamTexts.filter((c) => shownTeam(c.team!)).length
  };
  const textFrom = inbox?.texting.from ?? [];
  const admin = inbox?.me.role === 'admin';
  const showTexts = allTexts.length > 0 || inbox?.texting.numbers === 'all' || (Array.isArray(inbox?.texting.numbers) && inbox.texting.numbers.length > 0);

  const dialogs = (
    <>
      {dialog === 'channel' && (
        <NewChannel
          domain={domain}
          company={company}
          onClose={() => setDialog(null)}
          onMade={(id) => {
            setDialog(null);
            void load();
            navigate(`${base}/${id}`);
          }}
        />
      )}
      {dialog === 'dm' && (
        <NewDm
          domain={domain}
          onClose={() => setDialog(null)}
          onStarted={(id) => {
            setDialog(null);
            void load();
            navigate(`${base}/${id}`);
          }}
        />
      )}
      {dialog === 'text' && (
        <NewText
          domain={domain}
          from={textFrom}
          preset={textPreset}
          onClose={() => {
            setDialog(null);
            setTextPreset(null);
          }}
          onStarted={(id) => {
            setDialog(null);
            setTextPreset(null);
            void load();
            navigate(`${base}/${id}`);
          }}
        />
      )}
      {dialog === 'search' && (
        <SearchMessages
          domain={domain}
          myId={myId}
          onClose={() => setDialog(null)}
          onOpen={(r) => {
            setDialog(null);
            const m = r.message;
            navigate(`${base}/${r.conversation.id}?m=${encodeURIComponent(m.id)}${m.threadRootId && !m.alsoInChannel ? `&thread=${encodeURIComponent(m.threadRootId)}` : ''}`);
          }}
        />
      )}
      {dialog === 'browse' && inbox && (
        <BrowseChannels
          domain={domain}
          channels={inbox.channels}
          admin={admin}
          onClose={() => setDialog(null)}
          onOpen={(id) => {
            setDialog(null);
            void load();
            navigate(`${base}/${id}`);
          }}
        />
      )}
    </>
  );

  // A phone with no conversation open: the phone app's Home, or its DMs tab (MessagesPhone).
  if (!wide && !openId && inbox) {
    const props = {
      domain,
      base,
      company,
      myId,
      inbox,
      presence,
      admin,
      showTexts,
      channels,
      dms,
      texts,
      archivedTexts,
      canStartText: textFrom.length > 0,
      onDialog: setDialog,
      onChanged: loadSoon
    };
    return (
      <SmartContext.Provider value={smart /* smart messaging */}>
        {view === 'dms' ? <PhoneDms {...props} /> : <PhoneHome {...props} />}
        {dialogs}
      </SmartContext.Provider>
    );
  }

  return (
    <IsAdmin.Provider value={admin}>
    <SmartContext.Provider value={smart /* smart messaging */}>
      <div className="msg-page">
        <PageHeader eyebrow={company} title="Messages" />
        {error && !inbox ? (
          <div className="content">
            <div className="card">
              <LoadError error={error} retry={() => void load()} />
            </div>
          </div>
        ) : !inbox ? (
          <Loading label="Loading messages" />
        ) : (
          <div className={`msg${openId ? ' has-open' : ''}`}>
            <nav className="msg-side" aria-label="Conversations">
              <DesktopNotifyOffer />
              {/* File retention's first week (2936): the company's admins are told before anything goes. */}
              {admin && smart.marks?.files?.startsAt && Date.parse(smart.marks.files.startsAt) > Date.now() && (
                <p className="msg-side-note" role="status">
                  {`From ${dateOnly(smart.marks.files.startsAt)}, files here are kept ${smart.marks.files.keepDays} days; older ones are removed. `}
                  <Link to={`/t/${domain}/settings`}>Change it in Settings</Link>
                </p>
              )}
              <MyStatus domain={domain} myId={myId} name={inbox.me.name} presence={presence} />
              <button type="button" className="msg-search-open" onClick={() => setDialog('search')}>
                <Icon name="search" size={15} strokeWidth={2.2} />
                Search messages
              </button>
              <SideSection
                title="Channels"
                actions={
                  <>
                    <button type="button" className="msg-side-btn" aria-label="Browse channels" title="Browse channels" onClick={() => setDialog('browse')}>
                      <Icon name="search" size={15} strokeWidth={2.2} />
                    </button>
                    {admin && (
                      <button type="button" className="msg-side-btn" aria-label="New channel" title="New channel" onClick={() => setDialog('channel')}>
                        <Icon name="plus" size={16} strokeWidth={2.4} />
                      </button>
                    )}
                  </>
                }
              >
                {channels.map((c) => (
                  <ConversationLink key={c.id} base={base} c={c} myId={myId} />
                ))}
                {!channels.length && (
                  <p className="msg-side-empty">
                    {/* smart messaging */ smart.emptyNote(inbox.conversations, 'channel') ?? (admin ? 'No channels yet. Make one with +.' : 'You’re not in any channels. Browse them to join.')}
                  </p>
                )}
              </SideSection>
              <SideSection
                title="Direct messages"
                actions={
                  <button type="button" className="msg-side-btn" aria-label="New message" title="New message" onClick={() => setDialog('dm')}>
                    <Icon name="plus" size={16} strokeWidth={2.4} />
                  </button>
                }
              >
                {dms.map((c) => (
                  <ConversationLink key={c.id} base={base} c={c} myId={myId} presence={presence} />
                ))}
                {!dms.length && <p className="msg-side-empty">{/* smart messaging */ smart.emptyNote(inbox.conversations, 'dm') ?? `Message someone in ${company} with +.`}</p>}
              </SideSection>
              {showTexts && (
                <SideSection
                  title="Texts"
                  actions={
                    textFrom.length > 0 && (
                      <button type="button" className="msg-side-btn" aria-label="Text a phone number" title="Text a phone number" onClick={() => setDialog('text')}>
                        <Icon name="plus" size={16} strokeWidth={2.4} />
                      </button>
                    )
                  }
                >
                  {myTexts.map((c) => (
                    <ConversationLink key={c.id} base={base} c={c} myId={myId} />
                  ))}
                  {!myTexts.length && !teamTexts.length && (
                    <p className="msg-side-empty">
                      {/* smart messaging */ smart.emptyNote(inbox.conversations, 'text') ??
                        `Texts to ${textFrom.length > 1 ? 'your numbers' : 'your number'} show here.${textFrom.length ? ' Text someone with +.' : ''}`}
                    </p>
                  )}
                  {archivedTexts.length > 0 && (
                    <>
                      <button type="button" className="msg-side-more" aria-expanded={showArchived} onClick={() => setShowArchived((v) => !v)}>
                        <Icon name={showArchived ? 'chev' : 'chevRight'} size={13} strokeWidth={2.4} />
                        Archived ({archivedTexts.length})
                      </button>
                      {showArchived && archivedTexts.map((c) => <ConversationLink key={c.id} base={base} c={c} myId={myId} />)}
                    </>
                  )}
                </SideSection>
              )}
              {teams.length > 0 && (
                <>
                  <TeamFilterBar value={teamFilter} onChange={setTeamFilter} counts={teamCounts} />
                  {teams.map((t) => (
                    <TeamGroup
                      key={t.extension}
                      team={t}
                      list={teamTexts.filter((c) => c.team!.extension === t.extension)}
                      filter={teamFilter}
                      domain={domain}
                      link={(c) => <ConversationLink key={c.id} base={base} c={c} myId={myId} />}
                      {...(textFrom.some((f) => f.team === t.extension)
                        ? {
                            onNewText: () => {
                              setTextPreset(textFrom.find((f) => f.team === t.extension && f.canText !== false)?.number ?? textFrom.find((f) => f.team === t.extension)!.number);
                              setDialog('text');
                            }
                          }
                        : {})}
                    />
                  ))}
                </>
              )}
              <SmartSide domain={domain} base={base} conversations={inbox.conversations} admin={admin} onChanged={loadSoon} /> {/* smart messaging */}
              {inbox.archive?.adminsSearch && <p className="msg-side-empty">Your company’s admins can search every archived message in History.</p>}
            </nav>
            <section className="msg-pane" aria-label={open ? open.title : 'Conversation'}>
              {openId ? (
                <Thread
                  key={openId}
                  domain={domain}
                  id={openId}
                  myId={myId}
                  company={company}
                  onBack={() => navigate(listToGoBackTo(base))}
                  onLeft={() => (void load(), navigate(base))}
                  onChanged={loadSoon}
                />
              ) : (
                <div className="msg-none">
                  <Icon name="chat" size={28} />
                  <p>Choose a conversation.</p>
                </div>
              )}
            </section>
          </div>
        )}
        {dialogs}
      </div>
    </SmartContext.Provider>
    </IsAdmin.Provider>
  );
}

function SideSection({ title, actions, children }: { title: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <div className="msg-side-section">
      <div className="msg-side-head">
        <h2>{title}</h2>
        {actions && <span className="msg-side-actions">{actions}</span>}
      </div>
      <div className="msg-side-list">{children}</div>
    </div>
  );
}

function ConversationLink({ base, c, myId, presence }: { base: string; c: ConversationView; myId: string; presence?: PresenceList | undefined }) {
  const others = c.people.filter((p) => p.id !== myId);
  // The conversation on screen is being read: no badge on it.
  const { conversation: openId } = useParams();
  const unread = c.id === openId ? 0 : c.unread;
  return (
    <NavLink to={`${base}/${c.id}`} className={`msg-link${unread ? ' unread' : ''}`}>
      <span className="msg-link-icon" aria-hidden="true">
        {c.kind === 'channel' ? (
          <Icon name={channelIcon(c.access)} size={16} />
        ) : c.kind === 'dm' ? (
          others.length === 1 ? (
            <Avatar id={others[0]!.id} name={others[0]!.name} small presence={presence?.people[others[0]!.id]} />
          ) : (
            <Icon name="users" size={15} />
          )
        ) : (
          <Icon name="phone" size={15} />
        )}
      </span>
      <span className="msg-link-title">{c.title}</span>
      {c.kind === 'dm' && others.length === 1 && <ListLocalTime zone={presence?.zones?.[others[0]!.id]} />}
      {c.kind === 'text' && <TextStateMark id={c.id} /> /* smart messaging */}
      {c.kind === 'text' && c.team && <TeamMark t={c.team} />}
      {unread > 0 && (
        <span className="msg-badge">
          {unread > 99 ? '99+' : unread}
          <span className="sr-only"> unread</span>
        </span>
      )}
    </NavLink>
  );
}

// --- presence -----------------------------------------------------------------------------------------

const SHOWN_AS: Record<ChatPresence['status'], string> = {
  online: 'Online',
  idle: 'Idle',
  dnd: 'Do not disturb',
  offline: 'Offline'
};

/** A status of their own that hasn't cleared yet. */
export function liveCustom(c: CustomStatus | null | undefined, now = Date.now()): CustomStatus | null {
  return c && (c.emoji || c.text) && !(c.clearAt && Date.parse(c.clearAt) <= now) ? c : null;
}

/** "4:12", "1h 05m". */
function callLength(since: string, now: number): string {
  const s = Math.max(0, Math.round((now - Date.parse(since)) / 1000));
  return s < 3600 ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : `${Math.floor(s / 3600)}h ${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}m`;
}

/**
 * "Online · On a call · 4:12" ("In a Patch · 4:12" where Live sessions are on, 1.13.0); for someone
 * with no phone (messaging only), "Online · Messaging only" (or "In a Patch").
 */
function presenceLine(p: ChatPresence | undefined, now: number, phoneless = false, sessions = false): string {
  if (!p) return phoneless ? 'Messaging only' : '';
  const patch = sessions && p.phone?.in === 'patch' && p.phone.state === 'talking';
  const busy = (what: string) => `${what}${p.phone?.since ? ` · ${callLength(p.phone.since, now)}` : ''}`;
  const phone = patch ? busy('In a Patch') : phoneless ? 'Messaging only' : p.phone ? (p.phone.state === 'ringing' ? 'Phone ringing' : busy('On a call')) : '';
  return [SHOWN_AS[p.status], phone].filter(Boolean).join(' · ');
}

export function customText(c: CustomStatus | null): string {
  return c ? `${c.emoji ?? ''} ${c.text}`.trim() : '';
}

/** The time now, ticking while `on` (a call's length). */
function useNow(ms: number, on: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) return;
    const t = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(t);
  }, [ms, on]);
  return now;
}

const CHOICES: { choice: StatusChoice; label: string; hint: string }[] = [
  {
    choice: 'online',
    label: 'Online',
    hint: 'Online while you use Stitch; idle after two minutes away.'
  },
  {
    choice: 'idle',
    label: 'Idle',
    hint: 'Shown as away, whatever you’re doing.'
  },
  {
    choice: 'dnd',
    label: 'Do not disturb',
    hint: 'Nothing buzzes, and calls go to voicemail. It’s the same switch as on My phone and your desk phone.'
  },
  {
    choice: 'invisible',
    label: 'Invisible',
    hint: 'Shown as offline. You can use Messages as usual.'
  }
];

const STATUS_PRESETS: { emoji: string; text: string; hours: number | null }[] = [
  { emoji: '🗓️', text: 'In a meeting', hours: 1 },
  { emoji: '🍽️', text: 'At lunch', hours: 1 },
  { emoji: '🚗', text: 'Commuting', hours: 1 },
  { emoji: '🏠', text: 'Working from home', hours: null },
  { emoji: '🤒', text: 'Out sick', hours: null },
  { emoji: '🌴', text: 'On vacation', hours: null }
];
const STATUS_EMOJI = ['🗓️', '🍽️', '🚗', '🏠', '🤒', '🌴', '🎧', '📞', '✈️', '🏥', '☕', '🎉', '💬', '⏳', '🦷', '📦'];
const CLEAR_AFTER: { value: string; label: string }[] = [
  { value: 'never', label: 'Don’t clear' },
  { value: '30m', label: '30 minutes' },
  { value: '1h', label: '1 hour' },
  { value: '4h', label: '4 hours' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This week' }
];
function clearAtFor(v: string, now = new Date()): string | null {
  const at = new Date(now);
  if (v === '30m') at.setMinutes(at.getMinutes() + 30);
  else if (v === '1h') at.setHours(at.getHours() + 1);
  else if (v === '4h') at.setHours(at.getHours() + 4);
  else if (v === 'today') at.setHours(23, 59, 0, 0);
  else if (v === 'week') {
    // The end of Sunday.
    at.setDate(at.getDate() + ((7 - at.getDay()) % 7));
    at.setHours(23, 59, 0, 0);
  } else return null;
  return at.toISOString();
}

/** You, at the top of the list: how you show (online, idle, do not disturb, invisible) and a status of your own. */
export function MyStatus({ domain, myId, name, presence }: { domain: string; myId: string; name: string; presence: PresenceList | undefined }) {
  const [open, setOpen] = useState(false);
  const choice = presence?.me.choice ?? 'online';
  const custom = liveCustom(presence?.me.custom);
  const shown = presence?.people[myId]?.status ?? 'online';
  const label = CHOICES.find((x) => x.choice === choice)?.label ?? 'Online';
  return (
    <>
      <button type="button" className="msg-me" aria-label={`Your status: ${label}${custom ? `, ${customText(custom)}` : ''}. Change it`} onClick={() => setOpen(true)}>
        <Avatar id={myId} name={name} small presence={{ status: choice === 'invisible' ? 'offline' : shown, phone: presence?.people[myId]?.phone ?? null }} />
        <span className="msg-me-text">
          <strong>{name}</strong>
          <span>{custom ? customText(custom) : label}</span>
        </span>
        <Icon name="edit" size={14} />
      </button>
      {open && <StatusDialog domain={domain} myId={myId} presence={presence} onClose={() => setOpen(false)} />}
    </>
  );
}

function StatusDialog({ domain, myId, presence, onClose }: { domain: string; myId: string; presence: PresenceList | undefined; onClose: () => void }) {
  const current = liveCustom(presence?.me.custom);
  const [choice, setChoice] = useState<StatusChoice>(presence?.me.choice ?? 'online');
  const [emoji, setEmoji] = useState<string | null>(current?.emoji ?? null);
  const [text, setText] = useState(current?.text ?? '');
  const [clear, setClear] = useState(current?.clearAt ? 'keep' : 'never');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const save = async (custom: CustomStatus | null) => {
    setBusy(true);
    setError('');
    try {
      const r = await api.setStatus({ choice, custom });
      const shown: ChatPresence['status'] = r.choice === 'invisible' ? 'offline' : r.choice === 'online' ? 'online' : r.choice;
      setMyPresence(domain, myId, { choice: r.choice, custom: r.custom }, shown);
      onClose();
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  };
  const clearAt = clear === 'keep' ? (current?.clearAt ?? null) : clearAtFor(clear);
  return (
    <Dialog
      open
      onClose={onClose}
      title="Your status"
      subtitle="What the people in your company see next to your name."
      foot={
        <>
          {current && (
            <Button disabled={busy} onClick={() => void save(null)}>
              Clear status
            </Button>
          )}
          <span className="grow" />
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={busy} onClick={() => void save(emoji || text.trim() ? { emoji, text: text.trim(), clearAt } : null)}>
            Save
          </Button>
        </>
      }
    >
      <div className="stack">
        <fieldset className="msg-choices">
          <legend>Show me as</legend>
          {CHOICES.map((x) => (
            <label key={x.choice} className="msg-choice">
              <input type="radio" name="msg-choice" checked={choice === x.choice} onChange={() => setChoice(x.choice)} />
              <span className={`msg-dot ${x.choice === 'invisible' ? 'offline' : x.choice} inline`} aria-hidden="true" />
              <span className="grow">
                <strong>{x.label}</strong>
                <span className="muted">{x.hint}</span>
              </span>
            </label>
          ))}
        </fieldset>
        <div className="field">
          <label htmlFor="msg-status-text">A status of your own (optional)</label>
          <div className="msg-status-row">
            <span className="msg-status-emoji" aria-hidden="true">
              {emoji ?? '💬'}
            </span>
            <input id="msg-status-text" className="input" value={text} maxLength={100} placeholder="What’s happening?" onChange={(e) => setText(e.target.value)} />
          </div>
          <div className="msg-status-emoji-list" role="group" aria-label="Emoji for your status">
            {STATUS_EMOJI.map((e) => (
              <button key={e} type="button" className={`emoji-cell${emoji === e ? ' on' : ''}`} aria-label={emojiName(e)} aria-pressed={emoji === e} onClick={() => setEmoji(emoji === e ? null : e)}>
                {e}
              </button>
            ))}
          </div>
        </div>
        <div className="msg-status-presets" role="group" aria-label="Quick statuses">
          {STATUS_PRESETS.map((p) => (
            <button
              key={p.text}
              type="button"
              className="msg-reaction"
              onClick={() => {
                setEmoji(p.emoji);
                setText(p.text);
                setClear(p.hours === 1 ? '1h' : p.hours === null ? 'never' : '4h');
              }}
            >
              <span aria-hidden="true">{p.emoji}</span> {p.text}
            </button>
          ))}
        </div>
        <SelectField
          label="Clear after"
          value={clear}
          onChange={(e) => setClear(e.target.value)}
          options={[
            ...(current?.clearAt
              ? [
                  {
                    value: 'keep',
                    label: `As set (${fullWhen(current.clearAt)})`
                  }
                ]
              : []),
            ...CLEAR_AFTER
          ]}
        />
        {error && <Notice tone="crit">{error}</Notice>}
      </div>
    </Dialog>
  );
}

/**
 * Who is in a conversation (or sees a text conversation), with how each shows now. In a channel,
 * people in it add others from the company (not an Everyone channel), and admins take people out.
 */
function MembersDialog({ domain, c, myId, presence, onClose, onChanged }: { domain: string; c: ConversationView; myId: string; presence: PresenceList | undefined; onClose: () => void; onChanged: () => void }) {
  const [people, setPeople] = useState<{ id: string; name: string; extension: string | null }[]>();
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [chosen, setChosen] = useState<string[]>([]);
  const [removing, setRemoving] = useState<{ id: string; name: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const now = useNow(1000, true);
  const sessions = useLiveSessions(domain);
  const load = useCallback(() => {
    api
      .conversationMembers(domain, c.id)
      .then((r) => setPeople(r.people))
      .catch((e) => setError(errorText(e)));
  }, [domain, c.id]);
  useEffect(() => load(), [load]);
  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      load();
      onChanged();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const order: Record<ChatPresence['status'], number> = {
    online: 0,
    dnd: 1,
    idle: 2,
    offline: 3
  };
  const sorted = [...(people ?? [])].sort((a, b) => order[presence?.people[a.id]?.status ?? 'offline'] - order[presence?.people[b.id]?.status ?? 'offline'] || a.name.localeCompare(b.name));
  const canAdd = c.kind === 'channel' && Boolean(c.canAdd);
  const canRemove = c.kind === 'channel' && Boolean(c.canRemove);
  if (adding) {
    return (
      <Dialog
        open
        onClose={() => setAdding(false)}
        title="Add people"
        subtitle={`To #${c.title}${c.access === 'private' ? ': they’ll see everything in it, earlier messages too' : ''}`}
        foot={
          <>
            <Button onClick={() => setAdding(false)}>Back</Button>
            <Button
              variant="primary"
              disabled={busy || !chosen.length}
              onClick={() =>
                void act(async () => {
                  await api.addChannelPeople(domain, c.id, chosen);
                  setChosen([]);
                  setAdding(false);
                })
              }
            >
              {chosen.length > 1 ? `Add ${chosen.length} people` : 'Add'}
            </Button>
          </>
        }
      >
        <ChannelPeoplePicker domain={domain} exclude={(people ?? []).map((p) => p.id)} chosen={chosen} onChange={setChosen} />
        {error && <Notice tone="crit">{error}</Notice>}
      </Dialog>
    );
  }
  return (
    <Dialog
      open
      onClose={onClose}
      title={c.kind === 'text' ? 'Who sees these texts' : 'People here'}
      subtitle={c.kind === 'channel' ? `#${c.title}${c.access === 'everyone' || c.isDefault ? ' · everyone in the company' : c.access === 'private' ? ' · private' : ''}` : c.title}
      foot={
        <>
          {canAdd && (
            <Button icon="plus" onClick={() => setAdding(true)}>
              Add people
            </Button>
          )}
          <span className="grow" />
          <Button onClick={onClose}>Close</Button>
        </>
      }
    >
      {!people && !error && <Loading label="Loading people" />}
      {removing && (
        <Notice tone="warn" title={`Take ${removing.name} out of #${c.title}?`}>
          {c.access === 'private' ? 'They won’t see it any more.' : 'They can join it again.'}{' '}
          <span className="msg-hook-confirm">
            <Button size="small" variant="danger" disabled={busy} onClick={() => void act(async () => (await api.removeChannelPerson(domain, c.id, removing.id), setRemoving(null)))}>
              Take out
            </Button>
            <Button size="small" variant="ghost" onClick={() => setRemoving(null)}>
              Keep them
            </Button>
          </span>
        </Notice>
      )}
      {error && <Notice tone="crit">{error}</Notice>}
      {people && (
        <ul className="msg-members">
          {sorted.map((u) => {
            const p = presence?.people[u.id];
            const custom = liveCustom(p?.custom, now);
            return (
              <li key={u.id} className="msg-member">
                <Avatar id={u.id} name={u.name} presence={p} />
                <span className="grow">
                  <strong>{u.name}</strong>
                  <span className="muted">
                    {/* [messaging-only] no extension: "Messaging only", never a phone status */}
                    {[presenceLine(p, now, !u.extension, sessions), custom ? customText(custom) : ''].filter(Boolean).join(' · ') || ' '}
                  </span>
                </span>
                {u.extension && <span className="muted mono">Ext {u.extension}</span>}
                {canRemove && u.id !== myId && (
                  <button type="button" className="msg-tool" aria-label={`Take ${u.name} out of #${c.title}`} title="Take out of the channel" disabled={busy} onClick={() => setRemoving({ id: u.id, name: u.name })}>
                    <Icon name="x" size={14} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Dialog>
  );
}

/** Links in what people write become links (web addresses only); everything else stays text. */
function Linkified({ text }: { text: string }) {
  const parts: ReactNode[] = [];
  const re = /\bhttps?:\/\/[^\s<>"']+/gi;
  let at = 0;
  for (const m of text.matchAll(re)) {
    // Trailing punctuation belongs to the sentence, not the link (as the server finds a link to preview).
    const url = trimLink(m[0]);
    const start = m.index ?? 0;
    let ok = false;
    try {
      const u = new URL(url);
      ok = u.protocol === 'http:' || u.protocol === 'https:';
    } catch {
      ok = false;
    }
    if (!ok) continue;
    if (start > at) parts.push(text.slice(at, start));
    parts.push(
      <a key={start} href={url} target="_blank" rel="noopener noreferrer nofollow">
        {url}
      </a>
    );
    at = start + url.length;
  }
  if (at < text.length) parts.push(text.slice(at));
  return <>{parts}</>;
}

const STATUS_LABEL: Record<string, string> = {
  sending: 'Sending…',
  sent: 'Sent',
  delivered: 'Delivered',
  received: 'Received',
  failed: 'Not delivered'
};

/** Turns "@Name" for the people a message mentions into highlighted names (yours stands out); the rest is linkified text. */
function RichText({ text, mentions, myId }: { text: string; mentions: { id: string; name: string }[]; myId: string }) {
  if (!mentions.length) return <Linkified text={text} />;
  const names = [...mentions].sort((a, b) => b.name.length - a.name.length);
  const re = new RegExp(`@(${names.map((n) => n.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'g');
  const parts: ReactNode[] = [];
  let at = 0;
  for (const m of text.matchAll(re)) {
    const start = m.index ?? 0;
    const who = names.find((n) => n.name === m[1]);
    if (!who) continue;
    if (start > at) parts.push(<Linkified key={`t${at}`} text={text.slice(at, start)} />);
    parts.push(
      <span key={`m${start}`} className={`msg-mention${who.id === myId ? ' me' : ''}`}>
        @{who.name}
      </span>
    );
    at = start + m[0].length;
  }
  if (at < text.length) parts.push(<Linkified key={`t${at}`} text={text.slice(at)} />);
  return <>{parts}</>;
}

/**
 * What someone wrote, as it shows: code blocks (between ``` fences) in monospace with Copy,
 * `inline code`, and everything else as words with links and @mentions. Never HTML.
 */
function MessageText({ text, mentions, myId }: { text: string; mentions: { id: string; name: string }[]; myId: string }) {
  const blocks = useMemo(() => (text.includes('`') ? messageBlocks(text) : null), [text]);
  if (!blocks) return <RichText text={text} mentions={mentions} myId={myId} />;
  return (
    <>
      {blocks.map((b, i) =>
        b.kind === 'code' ? (
          <CodeBlock key={i} code={b.code} lang={b.lang} />
        ) : (
          <span key={i} className="msg-text">
            {inlineCode(b.text).map((part, j) =>
              part.kind === 'code' ? (
                <code key={j} className="msg-code-inline">
                  {part.code}
                </code>
              ) : (
                <RichText key={j} text={part.text} mentions={mentions} myId={myId} />
              )
            )}
          </span>
        )
      )}
    </>
  );
}

/** Where a conversation's messages can be written now: joined, not archived, and for texts, allowed to send. */
function canWrite(c: ConversationView, texting: ConversationDetail['texting']): boolean {
  if (c.kind === 'channel') return c.joined && !c.archived;
  if (c.kind === 'text') return Boolean(texting?.canSend);
  return true;
}

const NOTIFY_CHOICES: { level: NotifyLevel; label: string; hint: string }[] = [
  {
    level: 'all',
    label: 'Every new message',
    hint: 'Your phone and browser are told about everything here.'
  },
  {
    level: 'mentions',
    label: 'Mentions only',
    hint: 'When someone @mentions you, or replies in a thread you’re in.'
  },
  {
    level: 'muted',
    label: 'Nothing',
    hint: 'Still in your list, with what’s unread, but never on your phone.'
  }
];

/** What this person is told about a conversation on their phone and in their browser. */
function NotifyMenu({ domain, c, onChanged }: { domain: string; c: ConversationView; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const wrap = useRef<HTMLDivElement>(null);
  const level = c.notify ?? c.notifyDefault;
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => !wrap.current?.contains(e.target as Node) && setOpen(false);
    const esc = (e: globalThis.KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  return (
    <div className="msg-menu-wrap" ref={wrap}>
      <button
        type="button"
        className={`msg-tool big${level === 'muted' ? ' muted' : ''}`}
        aria-label={`Notifications: ${NOTIFY_CHOICES.find((x) => x.level === level)?.label ?? ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Notifications"
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name={level === 'muted' ? 'bellOff' : 'bell'} size={17} />
      </button>
      {open && (
        <div className="msg-menu" role="menu" aria-label="Notifications">
          {NOTIFY_CHOICES.map((x) => (
            <button
              key={x.level}
              type="button"
              role="menuitemradio"
              aria-checked={level === x.level}
              className="msg-menu-item"
              onClick={async () => {
                setError('');
                try {
                  // Choosing the default goes back to following it.
                  await api.setNotify(domain, c.id, x.level === c.notifyDefault ? null : x.level);
                  setOpen(false);
                  onChanged();
                } catch (e) {
                  setError(errorText(e));
                }
              }}
            >
              <span className="msg-menu-check" aria-hidden="true">
                {level === x.level && <Icon name="check" size={14} strokeWidth={2.6} />}
              </span>
              <span className="grow">
                <strong>
                  {x.label}
                  {x.level === c.notifyDefault ? ' (default)' : ''}
                </strong>
                <span>{x.hint}</span>
              </span>
            </button>
          ))}
          {error && <p className="msg-compose-error">{error}</p>}
        </div>
      )}
    </div>
  );
}

function Thread({
  domain,
  id,
  myId,
  company,
  onBack,
  onLeft,
  onChanged
}: {
  domain: string;
  id: string;
  myId: string;
  company: string;
  onBack: () => void;
  onLeft: () => void;
  onChanged: () => void;
}) {
  const sessions = useLiveSessions(domain);
  const [params, setParams] = useSearchParams();
  const openThread = params.get('thread');
  const focus = params.get('m');
  const [detail, setDetail] = useState<ConversationDetail>();
  const [msgs, setMsgs] = useState<MessageView[]>([]);
  const [more, setMore] = useState(false);
  /** Opened at a message further back (search, a notification): newer ones aren't loaded yet. */
  const [newer, setNewer] = useState(false);
  const newerRef = useRef(false);
  newerRef.current = newer;
  const [error, setError] = useState<Error>();
  const [newFrom, setNewFrom] = useState<number | null>(null);
  const [settings, setSettings] = useState(false);
  const [busy, setBusy] = useState('');
  const [actionError, setActionError] = useState('');
  const scroller = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  const keep = useRef<number | null>(null);
  const readTo = useRef(0);
  const focused = useRef<string | null>(null);
  const [members, setMembers] = useState(false);
  /** Texts (1.12.30): naming the other number. */
  const [naming, setNaming] = useState(false);
  // Move to a team (1.13.0, 3073): a conversation on your own number.
  const [moving, setMoving] = useState(false);
  const [movedTo, setMovedTo] = useState('');
  /** The summary beside a shared conversation (1.12.32), open or closed on this device. */
  const [summaryOn, setSummaryOn] = useSummaryOpen();
  // On a phone it opens over the conversation instead.
  const wideThread = useWide();
  const [summarySheet, setSummarySheet] = useState(false);
  /** Summaries can't run here (not set up, or off): the lane steps aside until it's asked for. */
  const [laneOff, setLaneOff] = useState(false);
  const [laneAsked, setLaneAsked] = useState(false);
  const presence = usePresenceList(domain);
  // A direct message with one other person: how they show, and their own status, in the header.
  const otherId = detail?.conversation.kind === 'dm' && detail.conversation.people.length === 2 ? detail.conversation.people.find((p) => p.id !== myId)?.id : undefined;
  const otherPresence = otherId ? presence?.people[otherId] : undefined;
  // [messaging-only] a DM's people carry no phone flag: looked up in the company's people.
  const directory = usePeople(domain, Boolean(otherId));
  const otherPhoneless = directory.find((p) => p.id === otherId)?.phone === false;
  const now = useNow(1000, otherPresence?.phone?.state === 'talking');
  const clock = useClock();
  const otherZone = otherId ? presence?.zones?.[otherId] : undefined;

  const showThread = useCallback(
    (rootId: string | null) =>
      setParams(
        (p) => {
          const next = new URLSearchParams(p);
          if (rootId) next.set('thread', rootId);
          else next.delete('thread');
          next.delete('m');
          return next;
        },
        { replace: !rootId }
      ),
    [setParams]
  );

  const loadDetail = useCallback(async () => {
    try {
      setDetail(await api.conversation(domain, id));
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    }
  }, [domain, id]);

  // Opening at a message (or at the thread a reply is in): the page around it.
  const around = focus ? (openThread ?? focus) : null;
  const loadAll = useCallback(async () => {
    try {
      const [d, page] = await Promise.all([
        api.conversation(domain, id),
        api.conversationMessages(domain, id, {
          limit: 50,
          ...(around ? { around } : {})
        })
      ]);
      setDetail(d);
      setMsgs(page.messages);
      setMore(page.more);
      setNewer(Boolean(page.newer));
      setError(undefined);
      const read = d.conversation.lastReadSeq;
      readTo.current = Math.max(readTo.current, read);
      // A channel being looked at before joining has nothing "new" in it.
      const first = d.conversation.joined ? page.messages.find((m) => m.seq > read && m.kind !== 'system' && !(m.author.kind === 'person' && m.author.id === myId)) : undefined;
      setNewFrom(first ? first.seq : null);
      stick.current = true;
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    }
  }, [domain, id, myId, around]);
  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  useMessagesLive((e) => {
    if (e.type === 'reset') return void loadAll();
    if (e.tenant !== domain || e.conversationId !== id) return;
    if (e.type === 'conversation') return void loadDetail();
    const m = e.message;
    if (!m) return;
    // Replies only in a thread stay in the thread (their first message updates with the count).
    if (e.type === 'message' && m.threadRootId && !m.alsoInChannel) return;
    // Further back in the conversation: new ones wait until the newest are loaded.
    if (e.type === 'message' && newerRef.current) return;
    if (e.type === 'message') setMsgs((prev) => (prev.some((x) => x.id === m.id) ? prev.map((x) => (x.id === m.id ? m : x)) : [...prev, m].sort((a, b) => a.seq - b.seq)));
    // They texted: no longer archived (1.12.30).
    if (e.type === 'message' && m.text?.direction === 'in') setDetail((d) => (d?.conversation.archivedByMe ? { ...d, conversation: { ...d.conversation, archivedByMe: false } } : d));
    if (e.type === 'message.updated') setMsgs((prev) => prev.map((x) => (x.id === m.id ? updatedMessage(x, m, e.reaction) : x)));
  });
  // Your own reaction shows at once (and comes back off if it couldn't be saved).
  const onReaction = useCallback((messageId: string, change: ReactionChange) => setMsgs((prev) => prev.map((x) => (x.id === messageId ? { ...x, reactions: applyReaction(x.reactions, change) } : x))), []);

  // Read: once the newest message is on screen and the tab is in front.
  const last = msgs.length ? msgs[msgs.length - 1]!.seq : 0;
  useEffect(() => {
    if (!detail) return;
    const mark = () => {
      if (document.visibilityState !== 'visible' || last <= readTo.current || newerRef.current) return;
      readTo.current = last;
      void api.markRead(domain, id, last).catch(() => undefined);
      // Its notifications on this device are old news now.
      void clearNotificationsFor(id);
    };
    const t = window.setTimeout(mark, 300);
    document.addEventListener('visibilitychange', mark);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('visibilitychange', mark);
    };
  }, [last, detail, domain, id]);

  // Stay at the newest message unless the person has scrolled back; keep the place when older ones
  // load; and once, go to the message a link pointed at (from a notification or search).
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    if (focus && focused.current !== focus) {
      const target = document.getElementById(`msg-${focus}`);
      if (target && el.contains(target)) {
        focused.current = focus;
        stick.current = false;
        target.scrollIntoView({ block: 'center' });
        return;
      }
    }
    if (keep.current !== null) {
      el.scrollTop = el.scrollHeight - keep.current;
      keep.current = null;
    } else if (stick.current) el.scrollTop = el.scrollHeight;
  }, [msgs, focus]);
  const onScroll = () => {
    const el = scroller.current;
    if (el) stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  };

  const later = async () => {
    const lastMsg = msgs[msgs.length - 1];
    if (!lastMsg) return;
    try {
      const page = await api.conversationMessages(domain, id, {
        after: lastMsg.seq,
        limit: 100
      });
      setMsgs((prev) => [...prev, ...page.messages.filter((m) => !prev.some((x) => x.id === m.id))]);
      setNewer(page.more);
    } catch (e) {
      setActionError(errorText(e));
    }
  };

  const earlier = async () => {
    const first = msgs[0];
    if (!first) return;
    try {
      const page = await api.conversationMessages(domain, id, {
        before: first.seq,
        limit: 50
      });
      keep.current = scroller.current ? scroller.current.scrollHeight - scroller.current.scrollTop : null;
      setMsgs((prev) => [...page.messages.filter((m) => !prev.some((x) => x.id === m.id)), ...prev]);
      setMore(page.more);
    } catch (e) {
      setActionError(errorText(e));
    }
  };

  const act = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    setActionError('');
    try {
      await fn();
    } catch (e) {
      setActionError(errorText(e));
    } finally {
      setBusy('');
    }
  };

  if (error && !detail) return <LoadError error={error} retry={() => void loadAll()} />;
  if (!detail) return <Loading label="Loading the conversation" />;
  const c = detail.conversation;
  const texting = detail.texting;
  const name = c.kind === 'channel' ? `#${c.title}` : c.title;
  const otherCustom = liveCustom(otherPresence?.custom, now);
  const subtitle =
    c.kind === 'channel'
      ? c.access === 'private'
        ? c.topic
          ? `Private · ${c.topic}`
          : 'Private channel'
        : c.topic || (c.access === 'everyone' || c.isDefault ? `Everyone at ${company}` : 'Channel')
      : c.kind === 'dm'
        ? c.people.length > 2
          ? `${plural(c.people.length, 'person', 'people')}`
          : [presenceLine(otherPresence, now, otherPhoneless, sessions), otherCustom ? customText(otherCustom) : '', localTimeText(otherZone, clock)].filter(Boolean).join(' · ') || 'Direct message'
        : c.contactName
          ? `${formatPhoneNumber(c.otherNumber ?? '')} · texting from ${formatPhoneNumber(c.stitchNumber ?? '')}`
          : `Texting from ${formatPhoneNumber(c.stitchNumber ?? '')}`;
  const threads = c.kind !== 'text';
  const writable = canWrite(c, texting);
  const lane = Boolean(c.team?.summaries) && summaryOn && !openThread && wideThread && !laneOff;
  // Reactions stay between colleagues (on texts too), in a channel you've joined.
  const reactable = c.kind === 'channel' ? c.joined && !c.archived : true;

  return (
    <div className={`msg-thread${openThread || lane ? ' with-side' : ''}${lane ? ' with-summary' : ''}`}>
      <div className="msg-thread-main">
        <header className="msg-thread-head">
          <button type="button" className="icon-btn msg-back" aria-label="All conversations" onClick={onBack}>
            <Icon name="chevLeft" size={18} />
          </button>
          <span className="msg-thread-icon" aria-hidden="true">
            {c.kind === 'channel' ? (
              <Icon name={channelIcon(c.access)} size={18} />
            ) : c.kind === 'dm' && otherId ? (
              <Avatar id={otherId} name={c.title} small presence={otherPresence} />
            ) : c.kind === 'dm' ? (
              <Icon name="users" size={18} />
            ) : (
              <Icon name="phone" size={17} />
            )}
          </span>
          <div className="msg-thread-title">
            <h2>{name}</h2>
            <span>{subtitle}</span>
          </div>
          <div className="msg-thread-actions">
            <SmartTriage c={c} /> {/* smart messaging: Done, Snooze, Later */}
            <HeaderExtras domain={domain} conversation={c} myId={myId} />
            {c.archived && <Chip>Archived</Chip>}
            {texting?.optedOut && <Chip tone="warn">Opted out</Chip>}
            {c.kind === 'text' && <CallButton number={c.otherNumber ?? ''} label={c.title} /> /* 2973 */}
            {c.kind === 'text' && c.team?.summaries && !lane && (
              <button type="button" className="msg-tool big" aria-label="Show the summary" title="Summary (Stitch AI)" onClick={() => (wideThread ? (setLaneOff(false), setLaneAsked(true), setSummaryOn(true)) : setSummarySheet(true))}>
                <Icon name="spark" size={17} />
              </button>
            )}
            {c.kind === 'text' && (
              <button type="button" className="msg-tool big" aria-label={c.contactName ? 'Rename this contact' : 'Name this contact'} title={c.contactName ? 'Rename this contact' : 'Name this contact'} onClick={() => setNaming(true)}>
                <Icon name="edit" size={17} />
              </button>
            )}
            {c.kind === 'text' && !c.team && !c.archivedByMe && (
              <button type="button" className="msg-tool big" aria-label="Move to a team" title="Move to a team: a ring group or queue takes it from here" onClick={() => setMoving(true)}>
                <Icon name="arrowOut" size={17} />
              </button>
            )}
            {c.kind === 'text' &&
              (c.archivedByMe ? (
                <Button size="small" variant="ghost" icon="archive" disabled={busy !== ''} title="Back to your list of texts" onClick={() => act('unarchive', async () => (setDetail(await api.archiveText(domain, c.id, false)), onChanged()))}>
                  Unarchive
                </Button>
              ) : (
                <button
                  type="button"
                  className="msg-tool big"
                  aria-label="Archive these texts"
                  title="Archive: out of your list until they text again"
                  disabled={busy !== ''}
                  onClick={() => act('archive', async () => (setDetail(await api.archiveText(domain, c.id, true)), onChanged()))}
                >
                  <Icon name="archive" size={17} />
                </button>
              ))}
            {!(c.kind === 'dm' && otherId) && (
              <button type="button" className="msg-tool big" aria-label={c.kind === 'text' ? 'Who sees these texts' : 'People here'} title={c.kind === 'text' ? 'Who sees these texts' : 'People here'} onClick={() => setMembers(true)}>
                <Icon name="users" size={17} />
              </button>
            )}
            {(c.kind !== 'channel' || c.joined) && <NotifyMenu domain={domain} c={c} onChanged={() => void loadDetail()} />}
            {c.kind === 'channel' && c.canManage && (
              <Button size="small" variant="ghost" icon="sliders" aria-label="Channel settings" title="Channel settings" onClick={() => setSettings(true)}>
                <span className="hide-sm">Channel settings</span>
              </Button>
            )}
            {c.kind === 'channel' && c.joined && (c.canLeave ?? !c.isDefault) && (
              <Button size="small" variant="ghost" disabled={busy !== ''} onClick={() => act('leave', async () => (await api.leaveChannel(domain, c.id), onLeft()))}>
                Leave
              </Button>
            )}
          </div>
        </header>
        <SmartThreadBar c={c} /> {/* smart messaging */}
        {c.team && <TeamBar domain={domain} c={c} onDetail={(d) => (setDetail(d), onChanged())} />}
        <UnderHeaderExtras domain={domain} conversation={c} myId={myId} />
        <div className="msg-scroll" ref={scroller} onScroll={onScroll} data-private="">
          {more ? (
            <div className="msg-earlier">
              <Button size="small" variant="ghost" onClick={() => void earlier()}>
                Load earlier messages
              </Button>
            </div>
          ) : (
            <div className="msg-start">
              <span className="msg-start-icon" aria-hidden="true">
                {c.kind === 'channel' ? <Icon name={channelIcon(c.access)} size={24} /> : c.kind === 'dm' ? <Icon name="chat" size={22} /> : <Icon name="phone" size={22} />}
              </span>
              <strong>{c.kind === 'channel' ? `Welcome to #${c.title}` : c.kind === 'dm' ? c.title : `Texts with ${c.title}`}</strong>
              <span>
                {c.kind === 'channel'
                  ? c.access === 'private'
                    ? `This is the start of #${c.title}. It’s private: only the people in it can see it.`
                    : c.access === 'everyone' || c.isDefault
                      ? `This is the start of #${c.title}. Everyone at ${company} is in it.`
                      : `This is the start of #${c.title}. Everyone at ${company} can read it and join.`
                  : c.kind === 'dm'
                    ? 'This is the start of your direct messages. Only the people in it can see them.'
                    : `Texts between ${c.contactName ? `${c.contactName} (${formatPhoneNumber(c.otherNumber ?? '')})` : c.title} and your number ${formatPhoneNumber(c.stitchNumber ?? '')}. Texts can’t be unsent.`}
              </span>
            </div>
          )}
          <div className="msg-log" role="log" aria-label={`Messages in ${name}`}>
            {msgs.map((m, i) => {
              const prev = msgs[i - 1];
              const newDay = !prev || dayOf(prev.createdAt) !== dayOf(m.createdAt);
              const grouped = groupsWith(prev, m) && !newDay && m.seq !== newFrom;
              return (
                <div key={m.id}>
                  {newDay && (
                    <div className={`msg-day${m.seq === newFrom ? ' new' : ''}`} role="separator" aria-label={m.seq === newFrom ? `${dayHeading(m.createdAt)}, new messages` : dayHeading(m.createdAt)}>
                      <span>{dayHeading(m.createdAt)}</span>
                      {m.seq === newFrom && <span className="msg-new-tag">New</span>}
                    </div>
                  )}
                  {m.seq === newFrom && !newDay && (
                    <div className="msg-new" role="separator" aria-label="New messages">
                      <span className="msg-new-tag">New</span>
                    </div>
                  )}
                  {m.kind === 'system' && m.meta?.type === 'call.offer' ? (
                    <CallOfferCard domain={domain} message={m} myId={myId} />
                  ) : m.kind === 'system' ? (
                    <div className="msg-system" role="note">
                      <span className="msg-system-text">{m.body}</span>
                      <time dateTime={m.createdAt} title={fullWhen(m.createdAt)}>
                        {clockTime(m.createdAt)}
                      </time>
                      <SystemMessageExtras domain={domain} conversation={c} myId={myId} message={m} />
                      {m.meta?.type === 'text.moved' && Array.isArray(m.meta.history) && m.meta.history.length > 0 && (
                        <details className="msg-moved-history">
                          <summary>Earlier messages ({m.meta.history.length})</summary>
                          <ol>
                            {(m.meta.history as { at: string; from: string; body: string }[]).map((x, i) => (
                              <li key={i}>
                                <strong>{x.from}</strong> <span className="muted">{clockTime(x.at)}</span>: {x.body}
                              </li>
                            ))}
                          </ol>
                        </details>
                      )}
                    </div>
                  ) : (
                    <MessageItem
                      domain={domain}
                      conversationId={c.id}
                      m={m}
                      grouped={grouped}
                      myId={myId}
                      locked={!writable}
                      focused={m.id === focus}
                      textDelete={c.kind === 'text' && Boolean(texting?.canDelete)}
                      outsideName={c.contactName}
                      {...(reactable ? { onReaction } : {})}
                      {...(threads ? { onThread: showThread } : {})}
                    />
                  )}
                </div>
              );
            })}
          </div>
          {newer && (
            <div className="msg-earlier">
              <Button size="small" variant="ghost" onClick={() => void later()}>
                Load newer messages
              </Button>
            </div>
          )}
        </div>
        {actionError && (
          <div className="msg-error">
            <Notice tone="crit">{actionError}</Notice>
          </div>
        )}
        {c.kind === 'channel' && c.archived ? (
          <div className="msg-bar">
            <span>#{c.title} is archived: it can be read, but nothing new can be posted.</span>
            {c.canManage && (
              <Button
                size="small"
                disabled={busy !== ''}
                onClick={() =>
                  act(
                    'unarchive',
                    async () => (
                      await api.updateChannel(domain, c.id, {
                        archived: false
                      }),
                      await loadDetail(),
                      onChanged()
                    )
                  )
                }
              >
                Unarchive
              </Button>
            )}
          </div>
        ) : c.kind === 'channel' && !c.joined ? (
          <div className="msg-bar">
            <span>You’re reading #{c.title}.</span>
            <Button variant="primary" size="small" disabled={busy !== ''} onClick={() => act('join', async () => (await api.joinChannel(domain, c.id), await loadDetail(), onChanged()))}>
              Join #{c.title}
            </Button>
          </div>
        ) : c.kind === 'text' && texting && !texting.canSend ? (
          <div className="msg-bar warn" role="status">
            <Icon name={texting.optedOut ? 'lock' : 'info'} size={17} />
            <span>{texting.reason}</span>
            {texting.code === 'OWNED' && c.team?.canTake && (
              <Button size="small" disabled={busy !== ''} onClick={() => act('take', async () => (setDetail(await api.takeText(domain, c.id)), onChanged()))}>
                Take over
              </Button>
            )}
          </div>
        ) : (
          <>
            <LateLine domain={domain} c={c} myId={myId} />
            <Composer key={c.id} domain={domain} c={c} name={name} myId={myId} onSent={() => (stick.current = true)} />
          </>
        )}
      </div>
      {lane && <SummaryLane key={c.id} domain={domain} c={c} onClose={() => setSummaryOn(false)} {...(laneAsked ? {} : { onOff: () => setLaneOff(true) })} />}
      {summarySheet && !wideThread && c.team && (
        <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && setSummarySheet(false)}>
          <div className="summary-sheet" role="dialog" aria-modal="true" aria-label="Summary">
            <SummaryLane domain={domain} c={c} onClose={() => setSummarySheet(false)} />
          </div>
        </div>
      )}
      {openThread && threads && (
        <ThreadPanel key={openThread} domain={domain} c={c} name={name} rootId={openThread} myId={myId} writable={writable} reactable={reactable} focus={focus} onClose={() => showThread(null)} />
      )}
      {members && <MembersDialog domain={domain} c={c} myId={myId} presence={presence} onClose={() => setMembers(false)} onChanged={() => (void loadDetail(), onChanged())} />}
      {moving && (
        <MoveToTeamDialog
          domain={domain}
          conversationId={c.id}
          contact={name}
          onClose={() => setMoving(false)}
          onMoved={(_to, team) => {
            setMoving(false);
            setMovedTo(team);
            void loadDetail();
            onChanged();
          }}
        />
      )}
      {movedTo && (
        <div className="msg-moved-note" role="status">
          <Notice tone="ok">
            Moved to {movedTo}. They were told which number to expect.{' '}
            <button type="button" className="link-btn" onClick={() => setMovedTo('')}>
              OK
            </button>
          </Notice>
        </div>
      )}
      {naming && (
        <NameContact
          domain={domain}
          c={c}
          company={company}
          onClose={() => setNaming(false)}
          onSaved={(d) => {
            setNaming(false);
            setDetail(d);
            onChanged();
          }}
        />
      )}
      {settings && (
        <ChannelSettings
          domain={domain}
          c={c}
          company={company}
          onClose={() => setSettings(false)}
          onSaved={() => {
            setSettings(false);
            void loadDetail();
            onChanged();
          }}
        />
      )}
    </div>
  );
}

/** One author's messages a few minutes apart read as one block (notes and new days break it). */
function groupsWith(prev: MessageView | undefined, m: MessageView): boolean {
  if (!prev || prev.kind === 'system' || m.kind === 'system') return false;
  const key = (x: MessageView) => (x.author.kind === 'person' ? `p:${x.author.id ?? x.author.name}` : x.author.kind === 'app' ? `a:${x.author.id ?? ''}:${x.author.name}` : `o:${x.author.number}`);
  return key(prev) === key(m) && new Date(m.createdAt).getTime() - new Date(prev.createdAt).getTime() < 5 * 60_000 && !m.alsoInChannel && !(m.replies && !prev.replies);
}

/** A thread: the message answered, its replies, and a box to reply (optionally also to the conversation). */
function ThreadPanel({
  domain,
  c,
  name,
  rootId,
  myId,
  writable,
  reactable,
  focus,
  onClose
}: {
  domain: string;
  c: ConversationView;
  name: string;
  rootId: string;
  myId: string;
  writable: boolean;
  reactable: boolean;
  focus: string | null;
  onClose: () => void;
}) {
  const [data, setData] = useState<{
    root: MessageView;
    replies: MessageView[];
    more: boolean;
  }>();
  const [error, setError] = useState<Error>();
  const scroller = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  const keep = useRef<number | null>(null);
  const load = useCallback(async () => {
    try {
      setData(await api.thread(domain, c.id, rootId));
      setError(undefined);
      stick.current = true;
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    }
  }, [domain, c.id, rootId]);
  useEffect(() => {
    void load();
  }, [load]);
  useMessagesLive((e) => {
    if (e.type === 'reset') return void load();
    if (e.tenant !== domain || e.conversationId !== c.id || !e.message) return;
    const m = e.message;
    setData((prev) => {
      if (!prev) return prev;
      if (m.id === prev.root.id) return { ...prev, root: updatedMessage(prev.root, m, e.reaction) };
      if (m.threadRootId !== rootId) return prev;
      const has = prev.replies.some((x) => x.id === m.id);
      return {
        ...prev,
        replies: has ? prev.replies.map((x) => (x.id === m.id ? updatedMessage(x, m, e.reaction) : x)) : [...prev.replies, m].sort((a, b) => a.seq - b.seq)
      };
    });
  });
  const onReaction = useCallback(
    (messageId: string, change: ReactionChange) =>
      setData((prev) => {
        if (!prev) return prev;
        const apply = (x: MessageView) => (x.id === messageId ? { ...x, reactions: applyReaction(x.reactions, change) } : x);
        return {
          ...prev,
          root: apply(prev.root),
          replies: prev.replies.map(apply)
        };
      }),
    []
  );
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    if (keep.current !== null) {
      el.scrollTop = el.scrollHeight - keep.current;
      keep.current = null;
    } else if (stick.current) el.scrollTop = el.scrollHeight;
  }, [data]);
  const earlier = async () => {
    const first = data?.replies[0];
    if (!first) return;
    try {
      const page = await api.thread(domain, c.id, rootId, first.seq);
      keep.current = scroller.current ? scroller.current.scrollHeight - scroller.current.scrollTop : null;
      setData((prev) =>
        prev
          ? {
              ...prev,
              replies: [...page.replies.filter((m) => !prev.replies.some((x) => x.id === m.id)), ...prev.replies],
              more: page.more
            }
          : prev
      );
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    }
  };
  return (
    <aside className="msg-side-thread" aria-label="Thread">
      <header className="msg-thread-head">
        <button type="button" className="icon-btn msg-back" aria-label="Back to the conversation" onClick={onClose}>
          <Icon name="chevLeft" size={18} />
        </button>
        <div className="msg-thread-title">
          <h2>Thread</h2>
          <span>{name}</span>
        </div>
        <button type="button" className="icon-btn msg-thread-close" aria-label="Close the thread" onClick={onClose}>
          <Icon name="x" size={17} />
        </button>
      </header>
      {data && <SmartThreadCatchUp c={c} rootId={rootId} replies={data.root.replies?.count ?? data.replies.length} />} {/* smart messaging */}
      <div
        className="msg-scroll"
        ref={scroller}
        data-private=""
        onScroll={() => {
          const el = scroller.current;
          if (el) stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
        }}
      >
        {error && !data ? (
          <LoadError error={error} retry={() => void load()} />
        ) : !data ? (
          <Loading label="Loading the thread" />
        ) : (
          <div className="msg-log" role="log" aria-label="Replies">
            <MessageItem domain={domain} conversationId={c.id} m={data.root} grouped={false} myId={myId} locked={!writable} focused={false} {...(reactable ? { onReaction } : {})} />
            <div className="msg-day" role="separator">
              <span>{data.replies.length || data.root.replies?.count ? plural(data.root.replies?.count ?? data.replies.length, 'reply', 'replies') : 'No replies yet'}</span>
            </div>
            {data.more && (
              <div className="msg-earlier">
                <Button size="small" variant="ghost" onClick={() => void earlier()}>
                  Load earlier replies
                </Button>
              </div>
            )}
            {data.replies.map((m, i) => (
              <MessageItem
                key={m.id}
                domain={domain}
                conversationId={c.id}
                m={m}
                grouped={groupsWith(data.replies[i - 1], m)}
                myId={myId}
                locked={!writable}
                focused={m.id === focus}
                {...(reactable ? { onReaction } : {})}
              />
            ))}
          </div>
        )}
      </div>
      {writable && data && (
        <Composer
          key={`${c.id}:${rootId}`}
          domain={domain}
          c={c}
          name={name}
          myId={myId}
          threadRootId={rootId}
          placeholder="Reply…"
          alsoLabel={c.kind === 'channel' ? `Also send to ${name}` : 'Also send to the conversation'}
          onSent={() => (stick.current = true)}
        />
      )}
    </aside>
  );
}

/**
 * A file's last week under the company's file retention (2936): "Removed in 3 days", "Removed
 * tonight"; nothing before then, or before the first removal (`startsAt`).
 */
export function fileLeft(postedAt: string | undefined, keep: { keepDays: number; startsAt: string | null } | undefined, now = Date.now()): string {
  if (!postedAt || !keep) return '';
  const due = Math.max(Date.parse(postedAt) + keep.keepDays * 86_400_000, keep.startsAt ? Date.parse(keep.startsAt) : 0);
  const days = (due - now) / 86_400_000;
  if (!Number.isFinite(days) || days >= 7) return '';
  if (days < 1) return 'Removed tonight';
  const n = Math.ceil(days);
  return `Removed in ${n} day${n === 1 ? '' : 's'}`;
}

function MessageItem({
  domain,
  conversationId,
  m,
  grouped,
  myId,
  locked,
  focused,
  textDelete = false,
  outsideName,
  onThread,
  onReaction
}: {
  domain: string;
  conversationId: string;
  m: MessageView;
  grouped: boolean;
  myId: string;
  locked: boolean;
  focused: boolean;
  /** Texts (1.12.30): the company lets people delete them. */
  textDelete?: boolean;
  /** Texts: the name the company gave the other number, shown for their texts. */
  outsideName?: string | null;
  /** In a conversation (not a thread panel): open this message's thread. */
  onThread?: (rootId: string) => void;
  /** Where people may react: shows a reaction on screen at once. */
  onReaction?: (messageId: string, change: ReactionChange) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState('');
  const [picker, setPicker] = useState<HTMLElement | null>(null);
  // A file or picture opened over the conversation, with the message's others to go through.
  const [viewing, setViewing] = useState<{ files: ViewerFile[]; start: number } | null>(null);
  const me = useContext(Me);
  const canReact = Boolean(onReaction) && m.kind === 'message' && !m.deletedAt;
  // File retention (2936): how long files are kept here, for the countdown in a file's last week.
  const fileKeep = useContext(SmartContext).marks?.files;
  // Files open in the viewer once they're checked for viruses (programs never: they're downloads).
  const viewable = m.files.filter((f) => (f.check ?? 'ok') === 'ok' && !f.program);
  const fileList: ViewerFile[] = viewable.map((f) => ({
    id: f.id,
    name: f.name,
    contentType: f.contentType,
    bytes: f.bytes,
    url: api.messageFileUrl(domain, conversationId, f.id, f.image),
    picture: f.image,
    left: fileLeft(f.postedAt, fileKeep)
  }));
  const mediaFiles: ViewerFile[] = (m.text?.media ?? [])
    .filter((x) => x.ready && /^image\//.test(x.contentType))
    .map((x, i) => ({ id: `media-${x.seq}`, name: `Picture ${i + 1}`, contentType: x.contentType, bytes: null, url: api.messageMediaUrl(domain, conversationId, m.id, x.seq), picture: true }));
  const react = async (emoji: string, on: boolean) => {
    const change = { emoji, on, person: { id: me.id, name: me.name } };
    setError('');
    onReaction?.(m.id, change);
    try {
      await api.react(domain, conversationId, m.id, emoji, on);
    } catch (e) {
      onReaction?.(m.id, { ...change, on: !on });
      setError(errorText(e));
    }
  };
  const name = m.author.kind === 'outside' ? outsideName || formatPhoneNumber(m.author.number) : m.author.name || 'Someone';
  const mine = m.author.kind === 'person' && m.author.id === myId;
  const canChange = mine && !m.text && !m.deletedAt && !locked;
  // A webhook's message: nobody edits it; the company's admins may delete it (1.12.19).
  const isAdmin = useContext(IsAdmin);
  const canDeleteApp = m.author.kind === 'app' && isAdmin && !m.deletedAt && !locked;
  const canReply = Boolean(onThread) && !m.threadRootId && !m.text && !locked;
  // A text: anyone who sees the conversation may delete it, for all of them, when the company allows (1.12.30).
  const canDeleteText = textDelete && Boolean(m.text) && !m.deletedAt;
  const undoUntil = m.deletedText?.undoUntil ? Date.parse(m.deletedText.undoUntil) : 0;
  // A chat message they deleted (1.13.0): theirs to bring back for a day, unless the company turned undo off.
  const myUndoUntil = m.deletedMessage?.byId === myId && m.deletedMessage.undoUntil ? Date.parse(m.deletedMessage.undoUntil) : 0;
  const [restoring, setRestoring] = useState(false);
  const restore = async () => {
    setRestoring(true);
    setError('');
    try {
      await (m.text ? api.restoreText(domain, conversationId, m.id) : api.restoreMessage(domain, conversationId, m.id));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setRestoring(false);
    }
  };
  return (
    <article
      id={`msg-${m.id}`}
      className={`msg-item${grouped ? ' grouped' : ''}${m.deletedAt ? ' deleted' : ''}${focused ? ' focused' : ''}${m.mentions.some((x) => x.id === myId) ? ' mentions-me' : ''}`}
      aria-label={`${name}, ${clockTime(m.createdAt)}`}
    >
      {m.alsoInChannel && m.threadRootId && onThread && (
        <button type="button" className="msg-in-thread" onClick={() => onThread(m.threadRootId!)}>
          <Icon name="chat" size={13} /> Replied in a thread
        </button>
      )}
      <div className="msg-gutter">
        {grouped ? (
          <time className="msg-gutter-time" dateTime={m.createdAt} title={fullWhen(m.createdAt)}>
            {clockTime(m.createdAt)}
          </time>
        ) : m.author.kind === 'app' ? (
          <AppAvatar domain={domain} conversationId={conversationId} messageId={m.id} author={m.author} app={m.app ?? null} />
        ) : (
          <Avatar id={m.author.kind === 'person' ? (m.author.id ?? m.author.name) : m.author.number} name={name} outside={m.author.kind === 'outside'} />
        )}
      </div>
      <div className="msg-main">
        {!grouped && (
          <div className="msg-head">
            {m.author.kind === 'person' && m.author.id && m.author.id !== myId ? <AuthorName domain={domain} id={m.author.id} name={name} /> : <strong className="msg-author">{name}</strong>}
            {m.text && <span className="msg-kind">{m.text.direction === 'in' ? 'Text' : m.text.kind === 'mms' ? 'Picture text' : 'Text'}</span>}
            {m.text && typeof m.meta?.auto === 'string' && (
              <span className="msg-kind auto" title={AUTO_WHY[m.meta.auto] ?? 'Sent by Stitch'}>
                Automatic
              </span>
            )}
            {m.author.kind === 'app' && (
              <span className="msg-kind from-app" title="Posted by a webhook">
                App
              </span>
            )}
            <time dateTime={m.createdAt} title={fullWhen(m.createdAt)}>
              {clockTime(m.createdAt)}
            </time>
          </div>
        )}
        {m.deletedAt && m.deletedText ? (
          <div className="msg-body muted msg-deleted-text">
            <em>Text deleted by {m.deletedText.by}</em>
            {undoUntil > Date.now() && (
              <Button size="small" variant="ghost" icon="undo" disabled={restoring} title={`You can bring it back until ${fullWhen(m.deletedText.undoUntil!)}`} onClick={() => void restore()}>
                Undo
              </Button>
            )}
          </div>
        ) : m.deletedAt && myUndoUntil > Date.now() ? (
          <div className="msg-body muted msg-deleted-text">
            <em>You deleted this message</em>
            <Button size="small" variant="ghost" icon="undo" disabled={restoring} title={`You can bring it back until ${fullWhen(m.deletedMessage!.undoUntil!)}`} onClick={() => void restore()}>
              Undo
            </Button>
          </div>
        ) : m.deletedAt ? (
          <p className="msg-body muted">
            <em>Message deleted</em>
          </p>
        ) : editing ? (
          <EditBox
            initial={m.body}
            onCancel={() => setEditing(false)}
            onSave={async (body) => {
              await api.editMessage(
                domain,
                conversationId,
                m.id,
                body,
                m.mentions.filter((x) => body.includes(`@${x.name}`)).map((x) => x.id)
              );
              setEditing(false);
            }}
          />
        ) : m.app ? (
          <div className="msg-body">
            <AppMessage domain={domain} conversationId={conversationId} messageId={m.id} app={m.app} from={{ name, at: m.createdAt }} />
          </div>
        ) : (
          m.body && (
            <div className="msg-body">
              <MessageText text={m.body} mentions={m.mentions} myId={myId} />
              {m.editedAt && (
                <span className="msg-edited" title={`Edited ${fullWhen(m.editedAt)}`}>
                  {' '}
                  (edited)
                </span>
              )}
            </div>
          )
        )}
        {m.preview && !m.deletedAt && !editing && (
          <LinkPreviewCard key={m.preview.url} domain={domain} conversationId={conversationId} messageId={m.id} preview={m.preview} canRemove={canChange} onError={setError} />
        )}
        {m.text && m.text.media.length > 0 && (
          <div className="msg-media">
            {m.text.media.map((x) =>
              x.ready ? (
                /^image\//.test(x.contentType) ? (
                  <button
                    key={x.seq}
                    type="button"
                    className="msg-file-pic"
                    aria-label={`Open the picture from ${name}`}
                    onClick={() => setViewing({ files: mediaFiles, start: Math.max(0, mediaFiles.findIndex((f) => f.id === `media-${x.seq}`)) })}
                  >
                    <img src={api.messageMediaUrl(domain, conversationId, m.id, x.seq)} alt={`Picture from ${name}`} loading="lazy" />
                  </button>
                ) : /^(video|audio)\//.test(x.contentType) ? (
                  // Videos and recordings play here (1.12.32, 2961).
                  <TextMedia key={x.seq} url={api.messageMediaUrl(domain, conversationId, m.id, x.seq)} contentType={x.contentType} from={name} />
                ) : (
                  <a key={x.seq} className="msg-file" href={api.messageMediaUrl(domain, conversationId, m.id, x.seq)} download>
                    <Icon name="download" size={15} /> Download the attachment
                  </a>
                )
              ) : (
                <span key={x.seq} className="msg-file muted">
                  {x.failed ? 'Something in this text couldn’t be kept.' : 'On its way…'}
                </span>
              )
            )}
          </div>
        )}
        {m.files.length > 0 && !m.deletedAt && (
          <div className="msg-files">
            {m.files.map((f) => {
              const check = f.check ?? 'ok';
              const at = viewable.indexOf(f);
              // Removed by file retention (2936): the message stays and says so.
              if (check === 'expired')
                return (
                  <span key={f.id} className="msg-file-card msg-file-expired">
                    <span className="msg-file-open">
                      <Icon name="clock" size={16} />
                      <span className="grow">
                        <strong>{f.name}</strong>
                        <span className="muted">{`File removed: kept ${f.keptDays ?? fileKeep?.keepDays ?? 90} days`}</span>
                      </span>
                    </span>
                  </span>
                );
              const left = fileLeft(f.postedAt, fileKeep);
              if (check !== 'ok')
                return (
                  <span key={f.id} className={`msg-file-card msg-file-${check}`} role={check === 'checking' ? 'status' : undefined}>
                    <span className="msg-file-open">
                      <Icon name={check === 'checking' ? 'lock' : 'alert'} size={16} />
                      <span className="grow">
                        <strong>{f.name}</strong>
                        <span className="muted">
                          {check === 'checking'
                            ? `Checking for viruses… · ${fileSize(f.bytes)}`
                            : check === 'virus'
                              ? `Removed: it had a virus${f.virus ? ` (${f.virus})` : ''}`
                              : 'Couldn’t be checked for viruses, so it can’t be opened'}
                        </span>
                      </span>
                    </span>
                  </span>
                );
              if (f.program)
                return (
                  <span key={f.id} className="msg-file-card msg-file-program">
                    <span className="msg-file-open">
                      <Icon name="alert" size={16} />
                      <span className="grow">
                        <strong>{f.name}</strong>
                        <span className="muted">Program · {fileSize(f.bytes)} · checked for viruses. Only open it if you trust {name} and expected it.</span>
                      </span>
                    </span>
                    <DownloadButton url={api.messageFileUrl(domain, conversationId, f.id)} name={f.name} bytes={f.bytes} label={`${f.name} (a program)`} />
                  </span>
                );
              return f.image ? (
                <button
                  key={f.id}
                  type="button"
                  className="msg-file-pic"
                  aria-label={`Open ${f.name}${left ? ` (${left})` : ''}`}
                  title={left || undefined}
                  onClick={() => setViewing({ files: fileList, start: at })}
                >
                  <img src={api.messageFileUrl(domain, conversationId, f.id, true)} alt={f.name} loading="lazy" />
                  {left && <span className="msg-file-left">{left}</span>}
                </button>
              ) : (
                <span key={f.id} className="msg-file-card">
                  <button type="button" className="msg-file-open" aria-label={`Open ${f.name}`} onClick={() => setViewing({ files: fileList, start: at })}>
                    <Icon name={kindIcon(f.contentType)} size={16} />
                    <span className="grow">
                      <strong>{f.name}</strong>
                      <span className="muted">
                        {kindWords(f.contentType)} · {fileSize(f.bytes)}
                        {left ? ` · ${left}` : ''}
                      </span>
                    </span>
                  </button>
                  <DownloadButton url={api.messageFileUrl(domain, conversationId, f.id)} name={f.name} bytes={f.bytes} />
                </span>
              );
            })}
          </div>
        )}
        {viewing && <FileViewer files={viewing.files} start={viewing.start} from={{ name, at: m.createdAt }} onClose={() => setViewing(null)} />}
        {/* A text to a person's number, followed here after a move to a team (1.13.0, 3073). */}
        {typeof m.meta?.sentTo === 'string' && <span className="msg-status">Sent to {typeof m.meta.movedFrom === 'string' && m.meta.movedFrom ? `${m.meta.movedFrom}’s number` : 'an earlier number'}</span>}
        {m.text?.direction === 'out' && !m.deletedAt && (
          <span className={`msg-status ${m.text.status}`}>
            {m.text.status === 'delivered' && <Icon name="check" size={13} strokeWidth={2.6} />}
            {STATUS_LABEL[m.text.status] ?? m.text.status}
            {m.text.status === 'failed' && m.text.error ? `: ${m.text.error}` : ''}
          </span>
        )}
        {m.reactions.length > 0 && !m.deletedAt && (
          <div className="msg-reactions">
            {m.reactions.map((r) => (
              <ReactionChip key={r.emoji} r={r} myId={myId} disabled={!canReact} onToggle={(on) => void react(r.emoji, on)} />
            ))}
            {canReact && (
              <button type="button" className="msg-reaction add" aria-label="Add a reaction" title="Add a reaction" onClick={(e) => setPicker(picker ? null : e.currentTarget)}>
                <Icon name="smile" size={15} />
              </button>
            )}
          </div>
        )}
        {m.replies && onThread && (
          <button type="button" className="msg-replies" onClick={() => onThread(m.id)}>
            <strong>{plural(m.replies.count, 'reply', 'replies')}</strong>
            {m.replies.lastAt && <span>Last reply {dayOf(m.replies.lastAt) === dayOf(new Date().toISOString()) ? clockTime(m.replies.lastAt) : dayHeading(m.replies.lastAt)}</span>}
          </button>
        )}
        {error && <span className="msg-status failed">{error}</span>}
      </div>
      {(canChange || canReply || canReact || canDeleteApp || canDeleteText) && !editing && (
        <div className="msg-tools">
          {canReact &&
            quickEmoji().map((e) => (
              <button
                key={e}
                type="button"
                className="msg-tool emoji"
                aria-label={`React with ${emojiName(e)}`}
                title={`React with ${emojiName(e)}`}
                onClick={() => void react(e, !m.reactions.some((r) => r.emoji === e && r.people.some((p) => p.id === myId)))}
              >
                {e}
              </button>
            ))}
          {canReact && (
            <button type="button" className="msg-tool" aria-label="Add a reaction" title="Add a reaction" onClick={(e) => setPicker(picker ? null : e.currentTarget)}>
              <Icon name="smile" size={15} />
            </button>
          )}
          {canReply && (
            <button type="button" className="msg-tool" aria-label="Reply in thread" title="Reply in thread" onClick={() => onThread?.(m.id)}>
              <Icon name="chat" size={15} />
            </button>
          )}
          {canChange && (
            <>
              <button type="button" className="msg-tool" aria-label="Edit message" title="Edit" onClick={() => setEditing(true)}>
                <Icon name="edit" size={15} />
              </button>
              <button type="button" className="msg-tool" aria-label="Delete message" title="Delete" onClick={() => setConfirm(true)}>
                <Icon name="trash" size={15} />
              </button>
            </>
          )}
          {canDeleteApp && (
            <button type="button" className="msg-tool" aria-label={`Delete this message from ${name}`} title="Delete" onClick={() => setConfirm(true)}>
              <Icon name="trash" size={15} />
            </button>
          )}
          {canDeleteText && (
            <button type="button" className="msg-tool" aria-label={m.text?.direction === 'in' ? `Delete this text from ${name}` : 'Delete this text'} title="Delete" onClick={() => setConfirm(true)}>
              <Icon name="trash" size={15} />
            </button>
          )}
        </div>
      )}
      {picker && (
        <EmojiPicker
          anchor={picker}
          title="Add a reaction"
          onClose={() => setPicker(null)}
          onPick={(e) => {
            setPicker(null);
            if (!m.reactions.some((r) => r.emoji === e && r.people.some((p) => p.id === myId))) void react(e, true);
          }}
        />
      )}
      {confirm && (
        <Dialog
          open
          onClose={() => setConfirm(false)}
          title={m.text ? 'Delete this text?' : 'Delete this message?'}
          foot={
            <>
              <Button onClick={() => setConfirm(false)}>Keep it</Button>
              <Button
                variant="danger"
                onClick={async () => {
                  setError('');
                  try {
                    await api.deleteMessage(domain, conversationId, m.id);
                  } catch (e) {
                    setError(errorText(e));
                  }
                  setConfirm(false);
                }}
              >
                Delete
              </Button>
            </>
          }
        >
          {m.text ? (
            <p>
              It’s deleted for everyone here who sees these texts. Any of them can bring it back for a day; after that it’s gone from Stitch. It isn’t unsent: the other phone
              keeps its copy, and so does the carrier.
            </p>
          ) : (
            <p>Everyone in the conversation will see that a message was deleted, but not what it said.</p>
          )}
        </Dialog>
      )}
    </article>
  );
}

/** A webhook's face: its own picture, else the message's (an emoji, or a picture Stitch fetched), else its initials. */
function AppAvatar({
  domain,
  conversationId,
  messageId,
  author,
  app
}: {
  domain: string;
  conversationId: string;
  messageId: string;
  author: Extract<MessageView['author'], { kind: 'app' }>;
  app: MessageView['app'] | null;
}) {
  const style = { '--msg-hue': hueOf(author.id ?? author.name) } as CSSProperties;
  const image = author.image !== null ? app?.images.find((x) => x.n === author.image && x.status === 'ok' && x.version) : undefined;
  return (
    <span className={`msg-avatar from-app${author.picture || image ? ' has-picture' : ''}`} style={style} aria-hidden="true">
      {author.picture && author.id ? (
        <img src={api.webhookPictureUrl(domain, conversationId, author.id, author.picture)} alt="" loading="lazy" decoding="async" />
      ) : image ? (
        <img src={api.appImageUrl(domain, conversationId, messageId, image.n, image.version!)} alt="" loading="lazy" decoding="async" />
      ) : author.emoji ? (
        <span className="msg-avatar-emoji">{author.emoji}</span>
      ) : (
        initials(author.name)
      )}
    </span>
  );
}

/** The icon on a file's card: what kind of file it is. */
function kindIcon(contentType: string): 'file' | 'video' | 'volume' {
  if (contentType.startsWith('video/')) return 'video';
  if (contentType.startsWith('audio/')) return 'volume';
  return 'file';
}


/** "You, Dana Ruiz and 3 others". */
function whoReacted(people: { id: string; name: string }[], myId: string): string {
  const names = [...people].sort((a, b) => (a.id === myId ? -1 : b.id === myId ? 1 : 0)).map((p) => (p.id === myId ? 'You' : p.name));
  if (names.length > 3) return `${names.slice(0, 3).join(', ')} and ${plural(names.length - 3, 'other')}`;
  return names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names.at(-1)}` : (names[0] ?? '');
}

/** One emoji on a message, how many, and who (on hover, and for screen readers); pressing it adds or takes back yours. */
function ReactionChip({ r, myId, disabled, onToggle }: { r: Reaction; myId: string; disabled: boolean; onToggle: (on: boolean) => void }) {
  const mine = r.people.some((p) => p.id === myId);
  const who = `${whoReacted(r.people, myId)} reacted with ${emojiName(r.emoji)}`;
  return (
    <button type="button" className={`msg-reaction${mine ? ' mine' : ''}`} aria-pressed={mine} aria-label={who} title={who} disabled={disabled} onClick={() => onToggle(!mine)}>
      <span className="msg-reaction-emoji" aria-hidden="true">
        {r.emoji}
      </span>
      <span className="msg-reaction-count" aria-hidden="true">
        {r.people.length}
      </span>
    </button>
  );
}

function EditBox({ initial, onSave, onCancel }: { initial: string; onSave: (body: string) => Promise<void>; onCancel: () => void }) {
  const [value, setValue] = useState(initial);
  const [error, setError] = useState('');
  const box = useRef<HTMLTextAreaElement>(null);
  const paste = usePasteAsCode({ area: box, limit: 4000, on: true, setText: (v) => setValue(v) });
  const save = async () => {
    if (!value.trim()) return setError('A message can’t be empty. Delete it instead.');
    try {
      await onSave(value);
    } catch (e) {
      setError(errorText(e));
    }
  };
  return (
    <div className="msg-edit">
      <textarea
        ref={box}
        className="input textarea"
        aria-label="Edit your message"
        value={value}
        autoFocus
        rows={Math.min(8, value.split('\n').length + 1)}
        maxLength={4000}
        onPaste={paste.onPaste}
        onChange={(e) => {
          setValue(e.target.value);
          paste.changed();
        }}
        onKeyDown={(e) => {
          if (paste.onKey(e)) return;
          if (e.key === 'Escape') onCancel();
          if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            void save();
          }
        }}
      />
      <span className="msg-edit-hint">
        {paste.note && (
          <span className="msg-code-note" role="status">
            Pasted as code.{' '}
            <button type="button" className="link-btn" onClick={paste.undo}>
              Undo
            </button>{' '}
            ·{' '}
          </span>
        )}
        Enter to save · Esc to cancel
        <span className="msg-edit-actions">
          <Button size="small" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="small" variant="primary" onClick={() => void save()}>
            Save
          </Button>
        </span>
      </span>
      {error && <span className="msg-status failed">{error}</span>}
    </div>
  );
}

type Person = {
  id: string;
  name: string;
  extension: string | null;
  phone?: boolean;
  me: boolean;
};
/** The company's people, once per company while the tab is open (for @mentions). */
const peopleCache = new Map<string, Promise<Person[]>>();
function usePeople(domain: string, on: boolean): Person[] {
  const [list, setList] = useState<Person[]>([]);
  useEffect(() => {
    if (!on) return;
    let alive = true;
    let p = peopleCache.get(domain);
    if (!p) {
      p = api.messagePeople(domain).then((r) => r.people);
      peopleCache.set(domain, p);
      p.catch(() => peopleCache.delete(domain));
    }
    p.then((l) => alive && setList(l)).catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [domain, on]);
  return list;
}

function Composer({
  domain,
  c,
  name,
  myId,
  threadRootId,
  placeholder,
  alsoLabel,
  onSent
}: {
  domain: string;
  c: ConversationView;
  name: string;
  myId: string;
  /** A reply in this thread. */
  threadRootId?: string;
  placeholder?: string;
  /** A reply can also go to the conversation: the label for that choice. */
  alsoLabel?: string;
  onSent: () => void;
}) {
  const draftKey = threadRootId ? `${c.id}:${threadRootId}` : c.id;
  const [text, setText] = useState(() => drafts.get(draftKey) ?? '');
  const [image, setImage] = useState<{ name: string; url: string } | null>(null);
  /** Files for the next message: uploaded as soon as they're chosen. */
  const [files, setFiles] = useState<{ key: string; name: string; bytes: number; id?: string; error?: string; progress?: number }[]>([]);
  // Big files on their way up in parts (1.12.16): removing one stops it.
  const bigUploads = useRef(new Map<string, UploadHandle>());
  const attach = useRef<HTMLInputElement>(null);
  const [also, setAlso] = useState(false);
  const [error, setError] = useState('');
  /** People picked with @, while their name is still in the text. */
  const [picked, setPicked] = useState<{ id: string; name: string }[]>([]);
  /** Typing "@sa": who might be meant, and where the @ is. */
  const [ask, setAsk] = useState<{ query: string; start: number } | null>(null);
  const [choice, setChoice] = useState(0);
  /** Messages go out one after another, in the order they were written, without waiting to type the next. */
  const queue = useRef<Promise<void>>(Promise.resolve());
  const area = useRef<HTMLTextAreaElement>(null);
  const file = useRef<HTMLInputElement>(null);
  /** The emoji button, while its picker is open (1.12.31). */
  const [emojiAt, setEmojiAt] = useState<HTMLElement | null>(null);
  const isText = c.kind === 'text';
  const limit = isText ? 1600 : 4000;
  // "Use this" in the summary beside a shared conversation (1.12.32) puts its reply here.
  useSuggestion(
    threadRootId ? '' : c.id,
    useCallback(
      (v: string) => {
        setText(v);
        drafts.set(draftKey, v);
        area.current?.focus();
      },
      [draftKey]
    )
  );
  const seg = useMemo(() => (isText ? segments(text) : null), [isText, text]);
  const [wantPeople, setWantPeople] = useState(false);
  const people = usePeople(domain, wantPeople);
  // Pasting code wraps it in a fence (not in texts: they go to phones as plain words).
  const paste = usePasteAsCode({
    area,
    limit,
    on: !isText,
    setText: (v, caret) => {
      setText(v);
      drafts.set(draftKey, v);
      caretAt.current = caret;
    }
  });
  const matches = useMemo(() => {
    if (!ask) return [];
    const q = ask.query.toLowerCase();
    return people.filter((p) => !p.me && (p.name.toLowerCase().split(/\s+/).some((w) => w.startsWith(q)) || p.name.toLowerCase().startsWith(q) || (p.extension ?? '').startsWith(q))).slice(0, 6);
  }, [ask, people]);

  /** Where the caret goes once a picked name is in the text (before the next key is typed). */
  const caretAt = useRef<number | null>(null);
  useLayoutEffect(() => {
    const el = area.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
    if (caretAt.current !== null) {
      el.focus();
      el.setSelectionRange(caretAt.current, caretAt.current);
      caretAt.current = null;
    }
  }, [text]);

  const lookForMention = (el: HTMLTextAreaElement) => {
    if (isText) return setAsk(null);
    const caret = el.selectionStart ?? el.value.length;
    const m = /(^|\s)@([^\s@]{0,30})$/.exec(el.value.slice(0, caret));
    if (m) {
      setWantPeople(true);
      setAsk({ query: m[2] ?? '', start: caret - (m[2]?.length ?? 0) - 1 });
      setChoice(0);
    } else setAsk(null);
  };
  const choose = (p: Person) => {
    const el = area.current;
    if (!el || !ask) return;
    const caret = el.selectionStart ?? text.length;
    const insert = `@${p.name} `;
    const next = text.slice(0, ask.start) + insert + text.slice(caret);
    setText(next);
    drafts.set(draftKey, next);
    setPicked((prev) => [...prev.filter((x) => x.id !== p.id), { id: p.id, name: p.name }]);
    setAsk(null);
    caretAt.current = ask.start + insert.length;
  };

  /** An emoji from the picker, where the caret was (or over what was selected); the box keeps focus. */
  const addEmoji = (emoji: string) => {
    const el = area.current;
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? start;
    const next = text.slice(0, start) + emoji + text.slice(end);
    setEmojiAt(null);
    if (next.length > limit) return;
    setText(next);
    drafts.set(draftKey, next);
    // The box takes focus back with the caret after the emoji (the picker closes onto the box).
    caretAt.current = start + emoji.length;
  };

  const uploading = files.some((f) => !f.id && !f.error);
  const ready = files.filter((f) => f.id);
  const addFiles = async (list: readonly File[]) => {
    const picked = list.slice(0, 10).map((file) => ({ file, key: `${Date.now()}-${Math.random()}` }));
    if (!picked.length) return;
    // Shown (and holding Send) at once, before the rules below are known, so nothing goes without them.
    setFiles((prev) => [...prev, ...picked.map(({ file, key }) => ({ key, name: file.name, bytes: file.size }))]);
    // What Messages takes here: up to 500 MB with the virus check and Block Storage, else 25 MB.
    let rules: Awaited<ReturnType<typeof fileRules>>;
    try {
      rules = await fileRules(domain);
    } catch (e) {
      const keys = new Set(picked.map((p) => p.key));
      setFiles((prev) => prev.map((x) => (keys.has(x.key) ? { ...x, error: errorText(e) } : x)));
      return;
    }
    for (const { file, key } of picked) {
      if (file.size > rules.maxBytes) {
        setFiles((prev) => prev.map((x) => (x.key === key ? { ...x, error: `Files are up to ${sizeWords(rules.maxBytes)}.` } : x)));
        continue;
      }
      if (file.size > ONE_GO_BYTES) setFiles((prev) => prev.map((x) => (x.key === key ? { ...x, progress: 0 } : x)));
      const settle = (p: Promise<MessageFileView>) =>
        p
          .then((f) => setFiles((prev) => prev.map((x) => (x.key === key ? { ...x, id: f.id, name: f.name, progress: undefined } : x))))
          .catch((e) => {
            if (e instanceof UploadCancelled) return;
            setFiles((prev) => prev.map((x) => (x.key === key ? { ...x, error: errorText(e), progress: undefined } : x)));
          })
          .finally(() => bigUploads.current.delete(key));
      if (file.size > ONE_GO_BYTES) {
        const h = uploadInParts(domain, c.id, file, rules.partBytes, (share) => setFiles((prev) => prev.map((x) => (x.key === key ? { ...x, progress: share } : x))));
        bigUploads.current.set(key, h);
        void settle(h.done);
      } else {
        void settle(api.uploadMessageFile(domain, c.id, file).then((r) => r.file));
      }
    }
  };
  /**
   * Files dropped on the conversation or pasted (2858): attached like the Attach button's. A text
   * takes one picture (sent as a picture text); other files go in channels and direct messages.
   */
  const takeFiles = (list: File[]) => {
    if (isText) {
      const pic = list.find((f) => /^image\//.test(f.type));
      if (!pic) {
        setError('A text can carry one picture. Other files go in channels and direct messages.');
        return;
      }
      setError('');
      shrinkPicture(pic).then(
        (url) => setImage({ name: pic.name, url }),
        (err) => setError(errorText(err))
      );
      return;
    }
    void addFiles(list);
  };
  const compose = useRef<HTMLDivElement>(null);
  const pane = threadRootId ? '.msg-side-thread' : '.msg-thread-main';
  const dropping = useFileDrop(compose, { on: true, pane, onFiles: takeFiles });
  const [paneEl, setPaneEl] = useState<HTMLElement | null>(null);
  useEffect(() => setPaneEl(compose.current?.closest<HTMLElement>(pane) ?? null), [pane]);
  /** Pasted files (a screenshot, files copied in Finder or Explorer) attach; pasted words stay words. */
  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const types = [...e.clipboardData.types];
    // Copying from a document or spreadsheet carries a picture of it too: that's words, not a file.
    if (types.includes('Files') && !types.includes('text/html')) {
      const got = pastedFiles(e.clipboardData);
      if (got.length) {
        e.preventDefault();
        takeFiles(got);
        return;
      }
    }
    paste.onPaste(e);
  };
  // Leaving the conversation stops big uploads still going.
  useEffect(() => {
    const running = bigUploads.current;
    return () => {
      for (const h of running.values()) h.cancel();
      running.clear();
    };
  }, []);
  const send = () => {
    const body = text;
    const picture = image;
    if (uploading) return;
    const sending = files.filter((f) => f.id);
    if (!body.trim() && !picture && !sending.length) return;
    const mentions = picked.filter((p) => body.includes(`@${p.name}`)).map((p) => p.id);
    const too = also;
    setText('');
    setImage(null);
    setFiles([]);
    setPicked([]);
    setAsk(null);
    setError('');
    paste.forget();
    drafts.delete(draftKey);
    onSent();
    area.current?.focus();
    queue.current = queue.current.then(async () => {
      try {
        await api.postMessage(domain, c.id, body, {
          ...(picture ? { image: picture.url } : {}),
          ...(threadRootId ? { threadRootId, alsoInChannel: too } : {}),
          ...(mentions.length ? { mentions } : {}),
          ...(sending.length ? { files: sending.map((f) => f.id!) } : {})
        });
      } catch (e) {
        // Not sent: it comes back to the box, with why.
        setError(errorText(e));
        setText((now) => now || body);
        if (picture) setImage((now) => now ?? picture);
        if (sending.length) setFiles((now) => (now.length ? now : sending));
      }
    });
  };
  const touch = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (paste.onKey(e)) return;
    if (ask && matches.length) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setChoice((i) => (i + (e.key === 'ArrowDown' ? 1 : matches.length - 1)) % matches.length);
        return;
      }
      if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Tab') {
        e.preventDefault();
        choose(matches[Math.min(choice, matches.length - 1)]!);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setAsk(null);
        return;
      }
    }
    // On a phone or tablet, Enter starts a new line (like any messaging app): the button sends.
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && (!touch || e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="msg-compose" data-private="" ref={compose}>
      {dropping &&
        paneEl &&
        createPortal(
          <div className="msg-drop" aria-hidden="true">
            <Icon name="upload" size={28} />
            <strong>{isText ? 'Drop a picture to send it in this text' : `Drop to attach to ${threadRootId ? 'your reply' : name}`}</strong>
            {!isText && <span>Up to 10 files at a time</span>}
          </div>,
          paneEl
        )}
      {error && (
        <p className="msg-compose-error" role="alert">
          {error}
        </p>
      )}
      {files.length > 0 && (
        <ul className="msg-attaching" aria-label="Files to send">
          {files.map((f) => (
            <li key={f.key} className={`msg-attaching-file${f.error ? ' failed' : ''}`}>
              <Icon name={f.error ? 'alert' : f.id ? 'check' : 'upload'} size={14} />
              <span className="grow">
                <strong>{f.name}</strong>
                <span className="muted">
                  {f.error ?? (f.id ? fileSize(f.bytes) : f.progress !== undefined ? `Uploading ${Math.round(f.progress * 100)}% of ${fileSize(f.bytes)}` : 'Uploading…')}
                </span>
                {f.progress !== undefined && !f.error && (
                  <span className="msg-upload-bar" role="progressbar" aria-label={`Uploading ${f.name}`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(f.progress * 100)}>
                    <span style={{ width: `${Math.round(f.progress * 100)}%` }} />
                  </span>
                )}
              </span>
              <button
                type="button"
                className="msg-tool"
                aria-label={`Remove ${f.name}`}
                onClick={() => {
                  bigUploads.current.get(f.key)?.cancel();
                  bigUploads.current.delete(f.key);
                  setFiles((prev) => prev.filter((x) => x.key !== f.key));
                }}
              >
                <Icon name="x" size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {image && (
        <div className="msg-attached">
          <img src={image.url} alt="" />
          <span className="grow">{image.name}</span>
          <button type="button" className="msg-tool" aria-label="Remove the picture" onClick={() => setImage(null)}>
            <Icon name="x" size={15} />
          </button>
        </div>
      )}
      <div className="msg-compose-box">
        {ask && matches.length > 0 && (
          <ul className="msg-mention-pick" role="listbox" aria-label="People to mention">
            {matches.map((p, i) => (
              <li
                key={p.id}
                role="option"
                aria-selected={i === choice}
                className={i === choice ? 'on' : ''}
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(p);
                }}
              >
                <Avatar id={p.id} name={p.name} small />
                <span className="grow">{p.name}</span>
                {p.extension && <span className="muted mono">Ext {p.extension}</span>}
              </li>
            ))}
          </ul>
        )}
        <div className="msg-compose-row">
          {!isText && (
            <>
              <button type="button" className="msg-tool big" aria-label="Attach files" title="Attach files" onClick={() => attach.current?.click()}>
                <Icon name="upload" size={17} />
              </button>
              <input
                ref={attach}
                type="file"
                multiple
                hidden
                data-testid="msg-attach"
                onChange={(e) => {
                  // Copied first: clearing the input empties its list.
                  const list = e.target.files ? [...e.target.files] : [];
                  e.target.value = '';
                  void addFiles(list);
                }}
              />
            </>
          )}
          {isText && (
            <>
              <button type="button" className="msg-tool big" aria-label="Add a picture" title="Add a picture (sent as a picture text)" onClick={() => file.current?.click()}>
                <Icon name="upload" size={17} />
              </button>
              <input
                ref={file}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,image/heic"
                hidden
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (!f) return;
                  try {
                    setImage({ name: f.name, url: await shrinkPicture(f) });
                  } catch (err) {
                    setError(errorText(err));
                  }
                }}
              />
            </>
          )}
          <textarea
            ref={area}
            className="msg-input"
            rows={1}
            value={text}
            maxLength={limit}
            aria-label={threadRootId ? 'Reply in the thread' : `Message ${name}`}
            placeholder={placeholder ?? (isText ? `Text ${name}` : `Message ${name}`)}
            onPaste={onPaste}
            onChange={(e) => {
              setText(e.target.value);
              drafts.set(draftKey, e.target.value);
              paste.changed();
              lookForMention(e.target);
            }}
            onClick={(e) => lookForMention(e.currentTarget)}
            onBlur={() => window.setTimeout(() => setAsk(null), 150)}
            onKeyDown={onKey}
          />
          <button type="button" className="msg-tool big" aria-label="Add an emoji" title="Add an emoji" aria-expanded={Boolean(emojiAt)} onClick={(e) => setEmojiAt(emojiAt ? null : e.currentTarget)}>
            <Icon name="smile" size={17} />
          </button>
          {emojiAt && <EmojiPicker anchor={emojiAt} backTo={area.current} title="Add an emoji" onClose={() => setEmojiAt(null)} onPick={addEmoji} />}
          <button type="button" className="msg-send" aria-label={isText ? 'Send text' : threadRootId ? 'Send reply' : 'Send'} disabled={uploading || (!text.trim() && !image && !ready.length)} onClick={send}>
            <Icon name="send" size={17} />
          </button>
        </div>
      </div>
      <p className="msg-compose-hint">
        {paste.note && (
          <span className="msg-code-note" role="status">
            Pasted as code.{' '}
            <button type="button" className="link-btn" onClick={paste.undo}>
              Undo
            </button>
          </span>
        )}
        {alsoLabel && (
          <label className="msg-also">
            <input type="checkbox" checked={also} onChange={(e) => setAlso(e.target.checked)} /> {alsoLabel}
          </label>
        )}
        {isText ? (
          <>
            Texts can’t be unsent.{' '}
            {seg && seg.count > 0 && `${plural(seg.count, 'segment')}${seg.encoding === 'ucs2' ? ' (an emoji or special character makes them 70 characters each)' : ''}.`}
          </>
        ) : (
          !alsoLabel && (touch ? '@ to mention someone' : 'Enter to send · Shift+Enter for a new line · @ to mention someone')
        )}
      </p>
    </div>
  );
}

// --- dialogs ------------------------------------------------------------------------------------------

type Found = Awaited<ReturnType<typeof api.searchMessages>>['results'][number];

/** The words searched for, marked where they start a word in what was written (search matches word starts). */
function Marked({ text, words }: { text: string; words: string[] }) {
  if (!words.length) return <>{text}</>;
  const parts: ReactNode[] = [];
  const re = /[\p{L}\p{N}]+/gu;
  let at = 0;
  for (const m of text.matchAll(re)) {
    const w = m[0].toLowerCase();
    const hit = words.find((q) => w.startsWith(q));
    if (!hit) continue;
    const start = m.index ?? 0;
    if (start > at) parts.push(text.slice(at, start));
    parts.push(<mark key={start}>{text.slice(start, start + m[0].length)}</mark>);
    at = start + m[0].length;
  }
  if (at < text.length) parts.push(text.slice(at));
  return <>{parts}</>;
}

/** Up to ~180 characters of a message, around the first word searched for. */
function snippet(text: string, words: string[]): string {
  const one = text.replace(/\s+/g, ' ').trim();
  if (one.length <= 180) return one;
  const lower = one.toLowerCase();
  // Words are letters and digits only, so they're safe in a pattern as they are.
  const hits = words.map((w) => lower.search(new RegExp(`(^|[^\\p{L}\\p{N}])${w}`, 'u'))).filter((i) => i >= 0);
  const first = hits.length ? Math.min(...hits) : 0;
  const start = Math.max(0, first - 60);
  return `${start > 0 ? '…' : ''}${one.slice(start, start + 180)}${start + 180 < one.length ? '…' : ''}`;
}

/** Search every channel, your direct messages and the texts you see; choose a result to open it there. */
function SearchMessages({ domain, myId, onClose, onOpen }: { domain: string; myId: string; onClose: () => void; onOpen: (r: Found) => void }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<Found[]>();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const asked = useRef(0);
  const words = useMemo(() => [...new Set(q.toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? [])], [q]);
  useEffect(() => {
    if (!words.length) {
      setResults(undefined);
      return;
    }
    const n = ++asked.current;
    const t = window.setTimeout(async () => {
      setBusy(true);
      try {
        const r = await api.searchMessages(domain, q);
        if (n === asked.current) {
          setResults(r.results);
          setError('');
        }
      } catch (e) {
        if (n === asked.current) setError(errorText(e));
      } finally {
        if (n === asked.current) setBusy(false);
      }
    }, 300);
    return () => window.clearTimeout(t);
  }, [domain, q, words.length]);
  return (
    <Dialog open onClose={onClose} title="Search messages" subtitle="Every channel, your direct messages, and the texts you see." foot={<Button onClick={onClose}>Close</Button>}>
      <div className="stack">
        <Field label="Search for" type="search" value={q} placeholder="Words in a message" data-autofocus onChange={(e) => setQ(e.target.value)} />
        {error && <Notice tone="crit">{error}</Notice>}
        {busy && !results && <Loading label="Searching" />}
        {results && !results.length && <p className="muted">Nothing found. Search finds whole words and the start of words.</p>}
        {results && results.length > 0 && (
          <ul className="msg-found" data-private="" aria-label="Messages found">
            {results.map((r) => {
              const m = r.message;
              const who = m.author.kind === 'person' ? (m.author.id === myId ? 'You' : m.author.name || 'Someone') : m.author.kind === 'app' ? m.author.name : formatPhoneNumber(m.author.number);
              return (
                <li key={m.id}>
                  <button type="button" className="msg-found-row" onClick={() => onOpen(r)}>
                    <span className="msg-found-where">
                      {r.conversation.kind === 'channel' ? `#${r.conversation.title}` : r.conversation.title}
                      {m.threadRootId && !m.alsoInChannel ? ' · in a thread' : ''}
                    </span>
                    <span className="msg-found-who">
                      <strong>{who}</strong> <time dateTime={m.createdAt}>{fullWhen(m.createdAt)}</time>
                    </span>
                    <span className="msg-found-text">
                      <Marked text={snippet(plainPreview(m.body), words)} words={words} />
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Dialog>
  );
}

function NewChannel({ domain, company, onClose, onMade }: { domain: string; company: string; onClose: () => void; onMade: (id: string) => void }) {
  const [name, setName] = useState('');
  const [topic, setTopic] = useState('');
  const [access, setAccess] = useState<ChannelAccess>('open');
  const [people, setPeople] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const slug = channelSlug(name);
  const create = async () => {
    setBusy(true);
    setError('');
    try {
      onMade((await api.createChannel(domain, name, topic.trim() || undefined, { access, ...(access !== 'everyone' && people.length ? { people } : {}) })).conversation.id);
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title="New channel"
      subtitle="Choose who it’s for: everyone, anyone who joins, or only the people you add."
      foot={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!slug || busy} onClick={() => void create()}>
            {access === 'private' ? 'Create private channel' : 'Create channel'}
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field label="Name" value={name} maxLength={80} placeholder="front-desk" data-autofocus onChange={(e) => setName(e.target.value)} hint={slug ? `#${slug}` : 'Lower-case words with dashes, like front-desk.'} />
        <Field label="Topic (optional)" value={topic} maxLength={200} placeholder="What it’s for" onChange={(e) => setTopic(e.target.value)} />
        <KindChoice value={access} onChange={setAccess} company={company} />
        {access === 'private' && (
          <div className="msg-kind-people">
            <p className="field-label">People to add (you can add more later)</p>
            <ChannelPeoplePicker domain={domain} chosen={people} onChange={setPeople} />
          </div>
        )}
        {error && <Notice tone="crit">{error}</Notice>}
      </div>
    </Dialog>
  );
}

function NewDm({ domain, onClose, onStarted }: { domain: string; onClose: () => void; onStarted: (id: string) => void }) {
  const presence = usePresenceList(domain);
  // [messaging-only] phone: false = no extension: "Messaging only", no phone status, no Call button.
  const [people, setPeople] = useState<
    {
      id: string;
      name: string;
      extension: string | null;
      phone?: boolean;
      me: boolean;
    }[]
  >();
  const [chosen, setChosen] = useState<string[]>([]);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    api
      .messagePeople(domain)
      .then((r) => setPeople(r.people.filter((p) => !p.me)))
      .catch((e) => setError(errorText(e)));
  }, [domain]);
  const shown = (people ?? []).filter((p) => !filter.trim() || `${p.name} ${p.extension ?? ''}`.toLowerCase().includes(filter.trim().toLowerCase()));
  const start = async () => {
    setBusy(true);
    setError('');
    try {
      onStarted((await api.startDm(domain, chosen)).conversation.id);
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title="New message"
      subtitle="Only the people you choose see it. Up to 9 people."
      foot={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!chosen.length || busy} onClick={() => void start()}>
            {chosen.length > 1 ? `Message ${chosen.length} people` : 'Message'}
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field label="Find someone" type="search" value={filter} placeholder="Name or extension" data-autofocus onChange={(e) => setFilter(e.target.value)} />
        {!people && !error && <Loading label="Loading people" />}
        {people && !people.length && <p className="muted">Nobody else in your company uses Stitch yet.</p>}
        <div className="msg-pick" role="group" aria-label="People">
          {shown.map((p) => (
            <label key={p.id} className="msg-pick-row">
              <input
                type="checkbox"
                checked={chosen.includes(p.id)}
                disabled={!chosen.includes(p.id) && chosen.length >= 9}
                onChange={(e) => setChosen((prev) => (e.target.checked ? [...prev, p.id] : prev.filter((x) => x !== p.id)))}
              />
              <Avatar id={p.id} name={p.name} small presence={presence?.people[p.id]} />
              <span className="grow">{p.name}</span>
              {/* [messaging-only] */}
              {p.extension ? <span className="muted mono">Ext {p.extension}</span> : p.phone === false ? <span className="muted">Messaging only</span> : null}
            </label>
          ))}
        </div>
        {error && <Notice tone="crit">{error}</Notice>}
      </div>
    </Dialog>
  );
}

/**
 * Text a phone number (1.12.30, Teampace 2953): from one of the company numbers whose texts you get,
 * to a US or Canadian number, optionally named for the whole company. It opens the conversation
 * (made if new); the first text, words or a picture, is written there.
 */
function NewText({
  domain,
  from: all,
  preset,
  onClose,
  onStarted
}: {
  domain: string;
  from: NonNullable<MessagesInbox['texting']['from']>;
  preset?: string | null;
  onClose: () => void;
  onStarted: (id: string) => void;
}) {
  // Numbers that can text are chosen from; the others are listed with why (3147).
  const from = all.filter((f) => f.canText !== false);
  const cant = all.filter((f) => f.canText === false);
  const [number, setNumber] = useState((preset && from.some((f) => f.number === preset) ? preset : null) ?? from[0]?.number ?? '');
  const presetCant = preset ? cant.find((f) => f.number === preset) : undefined;
  const [to, setTo] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const digits = to.replace(/\D/g, '');
  const ready = Boolean(number) && (digits.length === 10 || (digits.length === 11 && digits.startsWith('1')));
  const start = async () => {
    if (!ready || busy) return;
    setBusy(true);
    setError('');
    try {
      onStarted((await api.startText(domain, { from: number, to, ...(name.trim() ? { name: name.trim() } : {}) })).conversation.id);
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  };
  // Where each number rings (1.12.31): "(202) 864-1280 · Front Office (ring group)".
  const label = (f: (typeof from)[number]) => (f.where ? `${f.label} · ${f.where}` : f.mine ? `${f.label} · your number` : f.label);
  const GROUPS = { mine: 'Your numbers', team: 'Your team’s numbers', company: 'Company numbers (they ring nobody in Stitch)' } as const;
  return (
    <Dialog
      open
      onClose={onClose}
      title="Text a phone number"
      subtitle="Everyone who gets that company number’s texts sees the conversation."
      foot={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!ready || busy} onClick={() => void start()}>
            Start texting
          </Button>
        </>
      }
    >
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          void start();
        }}
      >
        {presetCant && (
          <Notice tone="warn" title={`${label(presetCant)} can’t send texts yet`}>
            {presetCant.why}
          </Notice>
        )}
        {from.length > 1 ? (
          <SelectField
            label="From"
            value={number}
            options={from.map((f) => ({ value: f.number, label: label(f), ...(f.group ? { group: GROUPS[f.group] } : {}) }))}
            onChange={(e) => setNumber(e.target.value)}
          />
        ) : from.length === 1 ? (
          <p className="msg-new-text-from">
            <span className="muted">From</span> <strong>{label(from[0]!)}</strong>
          </p>
        ) : (
          !presetCant && <Notice tone="warn">None of the numbers whose texts you get can send texts yet.</Notice>
        )}
        {cant.length > 0 && (
          <div className="msg-new-text-cant">
            <span className="field-label">Can’t text yet</span>
            <ul>
              {cant.map((f) => (
                <li key={f.number}>
                  <strong>{label(f)}</strong>: {f.why}
                </li>
              ))}
            </ul>
          </div>
        )}
        <Field label="To" type="tel" inputMode="tel" autoComplete="off" value={to} maxLength={30} placeholder="(410) 555-0123" data-autofocus onChange={(e) => setTo(e.target.value)} hint="A US or Canadian number. Only text people who agreed to get texts from you." />
        <Field label="Their name (optional)" value={name} maxLength={60} placeholder="Beth Moore" onChange={(e) => setName(e.target.value)} hint="Shown instead of their number, to everyone here who texts with them." />
        <button type="submit" hidden />
        {error && <Notice tone="crit">{error}</Notice>}
      </form>
    </Dialog>
  );
}

/** Names a text conversation's other number for the whole company (1.12.30, Teampace 2954), or clears it. */
function NameContact({ domain, c, company, onClose, onSaved }: { domain: string; c: ConversationView; company: string; onClose: () => void; onSaved: (d: ConversationDetail) => void }) {
  const [name, setName] = useState(c.contactName ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const number = formatPhoneNumber(c.otherNumber ?? '');
  const save = async (next: string | null) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      onSaved(await api.nameContact(domain, c.id, next));
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={c.contactName ? `Rename ${c.contactName}` : `Name ${number}`}
      subtitle={`Everyone at ${company} who texts with ${number} sees this name. Changes are in Activity.`}
      foot={
        <>
          {c.contactName && (
            <Button variant="ghost" disabled={busy} onClick={() => void save(null)}>
              Remove the name
            </Button>
          )}
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={busy || !name.trim() || name.trim() === c.contactName} onClick={() => void save(name.trim())}>
            Save
          </Button>
        </>
      }
    >
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim() && name.trim() !== c.contactName) void save(name.trim());
        }}
      >
        <Field label="Name" value={name} maxLength={60} placeholder="Beth Moore" data-autofocus onChange={(e) => setName(e.target.value)} />
        <button type="submit" hidden />
        {error && <Notice tone="crit">{error}</Notice>}
      </form>
    </Dialog>
  );
}

function BrowseChannels({ domain, channels, admin, onClose, onOpen }: { domain: string; channels: ChannelListing[]; admin: boolean; onClose: () => void; onOpen: (id: string) => void }) {
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const live = channels.filter((c) => !c.archived);
  const archived = channels.filter((c) => c.archived);
  const row = (c: ChannelListing) => (
    <li key={c.id} className="msg-browse-row">
      <Icon name={channelIcon(c.access)} size={16} />
      <span className="grow">
        <strong>{c.title}</strong>
        <span className="muted">
          {c.access === 'private' ? 'Private · ' : c.access === 'everyone' || c.isDefault ? 'Everyone · ' : ''}
          {c.topic ? `${c.topic} · ` : ''}
          {plural(c.members, 'member')}
        </span>
      </span>
      {c.joined || c.archived ? (
        <Button size="small" onClick={() => onOpen(c.id)}>
          Open
        </Button>
      ) : (
        <Button
          size="small"
          variant="primary"
          disabled={busy !== ''}
          onClick={async () => {
            setBusy(c.id);
            setError('');
            try {
              await api.joinChannel(domain, c.id);
              onOpen(c.id);
            } catch (e) {
              setError(errorText(e));
              setBusy('');
            }
          }}
        >
          Join
        </Button>
      )}
    </li>
  );
  return (
    <Dialog open onClose={onClose} title="Channels" subtitle="Every channel you can join, and the private ones you’re in." foot={<Button onClick={onClose}>Close</Button>}>
      <div className="stack">
        {live.length ? <ul className="msg-browse">{live.map(row)}</ul> : <p className="muted">No channels yet.{admin ? ' Make one with + next to Channels.' : ''}</p>}
        {archived.length > 0 && (
          <>
            <h3 className="msg-browse-label">Archived (read-only)</h3>
            <ul className="msg-browse">{archived.map(row)}</ul>
          </>
        )}
        {error && <Notice tone="crit">{error}</Notice>}
      </div>
    </Dialog>
  );
}

function ChannelSettings({ domain, c, company, onClose, onSaved }: { domain: string; c: ConversationView; company: string; onClose: () => void; onSaved: () => void }) {
  const [tab, setTab] = useState<'settings' | 'webhooks'>('settings');
  const [name, setName] = useState(c.title);
  const [topic, setTopic] = useState(c.topic);
  const current: ChannelAccess = c.access ?? (c.isDefault ? 'everyone' : 'open');
  const [access, setAccess] = useState<ChannelAccess>(current);
  /** Private → Open or Everyone: asked first (its whole history opens to the company). */
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const slug = channelSlug(name);
  const changed = slug !== c.title || topic.trim() !== c.topic || access !== current;
  const run = async (set: { name?: string; topic?: string; archived?: boolean; access?: ChannelAccess }) => {
    setBusy(true);
    setError('');
    try {
      await api.updateChannel(domain, c.id, set);
      onSaved();
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  };
  const save = () => {
    if (current === 'private' && access !== 'private' && !asking) return setAsking(true);
    void run({ name, topic, ...(access !== current ? { access } : {}) });
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title="Channel settings"
      subtitle={`#${c.title}`}
      foot={
        tab === 'settings' ? (
          <>
            {!c.archived && (
              <Button variant="danger" disabled={busy} onClick={() => void run({ archived: true })}>
                Archive channel
              </Button>
            )}
            <span className="grow" />
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="primary" disabled={!slug || busy || !changed} onClick={save}>
              {asking ? `Yes, open it to ${company}` : 'Save'}
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>Done</Button>
        )
      }
    >
      <div className="msg-tabs" role="tablist" aria-label="Channel settings">
        <button type="button" role="tab" aria-selected={tab === 'settings'} className={tab === 'settings' ? 'on' : ''} onClick={() => setTab('settings')}>
          Settings
        </button>
        <button type="button" role="tab" aria-selected={tab === 'webhooks'} className={tab === 'webhooks' ? 'on' : ''} onClick={() => setTab('webhooks')}>
          Webhooks
        </button>
      </div>
      {tab === 'webhooks' ? (
        <ChannelWebhooks domain={domain} c={c} />
      ) : (
        <div className="stack">
          <Field label="Name" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} hint={slug ? `#${slug}` : 'Lower-case words with dashes.'} data-autofocus />
          <Field label="Topic" value={topic} maxLength={200} onChange={(e) => setTopic(e.target.value)} />
          {c.isDefault ? (
            <p className="muted small-print">
              <Icon name="users" size={13} /> #{c.title} is for everyone at {company}: everyone is in it, and that can’t change.
            </p>
          ) : (
            <KindChoice
              value={access}
              onChange={(a) => {
                setAccess(a);
                setAsking(false);
              }}
              company={company}
            />
          )}
          {asking && (
            <Notice tone="warn" title="Open this private channel?">
              Everyone at {company} will be able to read its history. Choose <strong>Yes, open it to {company}</strong> to go ahead.
            </Notice>
          )}
          {access === 'everyone' && current !== 'everyone' && !asking && <p className="muted small-print">Everyone at {company} will be added to it now, and nobody will be able to leave it.</p>}
          {current === 'open' && access === 'private' && <p className="muted small-print">The people in it now stay in it; nobody else will see it.</p>}
          <p className="muted small-print">Archiving keeps everything in it readable, and takes it off everyone’s list. You can bring it back from Channels.</p>
          {error && <Notice tone="crit">{error}</Notice>}
        </div>
      )}
    </Dialog>
  );
}
